<script>
  import { onMount } from 'svelte';
  import { canadaTaxConfigForXeroBill, provinceBillTaxName, normalizeProvince, splitXeroLineTax } from '../lib/canadaTax.js';
  import Layout from '../components/Layout.svelte';
  import { navigate, useLocation } from 'svelte-routing';
  import { getJobValueFromLineItems, getPOValueFromLineItems, getAllPOValuesFromLineItems, normalizePO, jobMatchesTrackingValue } from '../lib/billMatching.js';
  import { normalizeForComparison, stripAllWhitespace, findVendorCandidates, parseCreatedVendor, isRequiredCustomFieldResult, mergeRequiredCustomFields, customFieldValuesFromSelections, requiredCustomFieldsFilled } from '../lib/vendorMatch.js';
  import RequiredVendorCustomField from '../components/RequiredVendorCustomField.svelte';

  const location = useLocation();

  let selectedBills = [];
  let currentBillIndex = 0;
  let currentBill = null;
  let error = null;

  // JobTread integration variables
  let customers = [];
  let selectedCustomer = null;
  let vendorOrders = [];
  let selectedVendorOrder = null;

  let totalAssigned = 0;
  let lineItemsData = null;
  let allItemsAssigned = false;
  let assignedItems = [];
  let vendorBillCreated = false;
  let isAutoMatched = false;
  let isLoadingVendorOrders = false;
  let isCreatingVendorBill = false;

  let matchedPOCount = { found: 0, total: 0 };  // Track how many POs were matched
  let multipleMatchedPOs = false;  // Flag for multiple matched POs
  
  // Vendor matching variables
  let showVendorMatching = false;
  let jtVendors = [];
  let filteredVendors = [];
  let vendorSearchQuery = "";
  let selectedVendorId = null;
  let isLoadingVendors = false;
  let isCreatingVendor = false;
  let autoMatchedVendor = false; // Track if vendor was automatically matched
  let isAutoMatchingVendor = false; // Track if currently searching for vendor match
  let autoMatchFailed = false; // Track if auto-matching failed to find a vendor
  let pendingVendorCandidates = []; // Fuzzy matches awaiting user confirmation
  let pendingVendorSourceName = ''; // The source name that produced the candidates
  let requiredVendorFields = [];
  let requiredVendorFieldValues = {};
  let requiredVendorError = '';
  let vendorAliases = {}; // Cached known aliases from DynamoDB
  let vendorAliasesLoaded = false; // Whether aliases have been fetched this session

  // New variables for handling multiple vendor bills
  let matchedVendorBills = [];  // Array to store multiple matched vendor bills
  let selectedVendorBillForAdd = null;  // Selected vendor bill in the "Add Vendor Bill" dialog
  let showInlineAddForm = false;  // Flag to show/hide the inline add form
  let lineItemVendorBillSelections = {};  // Map of lineItemID to selected vendor bill
  let isJobMode = false;  // Toggle state for PO mode (false) vs Job mode (true)
  let previousMode = false;  // Track previous mode to detect actual changes
  let billTrackingSettings = null;
  
  // JobTread user/organization info for document creation
  let jobtreadUserName = "BuildZilla";  // Default fallback
  let jobtreadOrgName = "BuildZilla";  // Default fallback
  
  // Reactive statement to handle mode switching
  $: if (currentBill && currentBill.lineItems && isJobMode !== previousMode) {
    if (isJobMode) {
      // Switching TO Job Mode - reset all PO mode data
      console.log('🔄 Switching to Job Mode - resetting PO mode data');
      
      // Reset all line item assignments
      lineItemVendorBillSelections = {};
      
      // Clear assigned items
      assignedItems = [];
      
      // Reset Customer/PO selections
      selectedCustomer = null;
      selectedVendorOrder = null;
      customerSearchQuery = '';
      
      // Reset totals
      totalAssigned = 0;
      allItemsAssigned = false;
      
      console.log('✅ Job Mode cleanup complete - starting fresh');
      
      // Now check if we can auto-select jobs from the matching section
      const jobsInMatching = matchedVendorBills.filter(bill => bill.type === 'job');
      
      if (jobsInMatching.length > 0) {
        // Use the first job from the matching section (only auto-select if there's an actual match)
        const selectedJob = jobsInMatching[0];
        currentBill.lineItems.forEach(lineItem => {
          lineItemVendorBillSelections[lineItem.lineItemID] = selectedJob.jobId;
        });
        // Trigger reactivity
        lineItemVendorBillSelections = { ...lineItemVendorBillSelections };
        
        // Populate the Job search field with the matched job
        customerSearchQuery = selectedJob.jobName || selectedJob.name || '';
        selectedCustomer = selectedJob.jobId;
        
        // Fetch cost groups for the selected job
        fetchJobCostGroups(selectedJob.jobId);
        
        console.log('✅ Auto-selected job from matching section:', selectedJob.jobName);
      } else {
        // No jobs in matching - re-trigger auto-detection from line item tracking
        tryAutoMatchJobFromTrackingCategory();
      }
    } else {
      // Switching TO PO Mode - reset all Job mode data
      console.log('🔄 Switching to PO Mode - resetting Job mode data');
      
      // Reset all line item assignments
      lineItemVendorBillSelections = {};
      
      // Clear assigned items
      assignedItems = [];
      
      // Reset Customer/PO selections
      selectedCustomer = null;
      selectedVendorOrder = null;
      customerSearchQuery = '';
      
      // Reset totals
      totalAssigned = 0;
      allItemsAssigned = false;
      
      // Reset job-specific data
      lineItemsData = null;
      
      console.log('✅ PO Mode cleanup complete - starting fresh');
      
      // Re-trigger PO mode auto-detection from line item tracking
      runPOModeAutoMatch();
    }
    
    // Update previous mode
    previousMode = isJobMode;
  }

  $: hasJobInMatching = isJobMode && matchedVendorBills.some(bill => bill.type === 'job');
  $: hasSinglePOInMatching = !isJobMode && matchedVendorBills.filter(b => b.vendorOrderNumber).length === 1;

  // Dynamic tax configuration based on user's country
  let taxConfig = {
    rate: 0.15,  // Default to NZ rate
    name: "GST", // Default to GST
    currency: "NZD", // Default to NZD
    locale: "en-NZ"  // Default to NZ locale
  };

  // Utility function to clean cost group names by removing Chinese characters and cleaning up formatting
  function cleanCostGroupName(name) {
    if (!name) return name;
    
    // Remove Chinese characters (Unicode range for CJK characters: \u4e00-\u9fff)
    // Also clean up common patterns like "- #" followed by Chinese chars
    return name
      .replace(/[-\s]*#[\u4e00-\u9fff]+$/g, '') // Remove "- #chinese" or "#chinese" at end
      .replace(/[\u4e00-\u9fff]+/g, '') // Remove any remaining Chinese characters
      .replace(/[-\s]+$/, '') // Remove trailing dashes and spaces
      .trim();
  }
  let userCountry = null;
  let userProvince = null;
  // Canada: each Xero bill carries its own rate (5% GST, 13% HST, 12% GST + PST
  // ...), so the tax config is set per bill from the bill's totals.
  let caBaseTaxConfig = null;
  // Canada: Xero purchase tax type -> non-recoverable (PST) %. Loaded once; null
  // until loaded (or for other countries), which keeps the standard display.
  let caNonRecoverableByTaxType = null;
  // PST split per Xero line (same maths as the sync), keyed by lineItemID.
  $: caLineSplits = (caNonRecoverableByTaxType && currentBill?.lineItems)
    ? Object.fromEntries(currentBill.lineItems.map(li => [li.lineItemID, splitXeroLineTax(li, currentBill.lineAmountTypes, caNonRecoverableByTaxType[li.taxType])]))
    : null;
  $: caBillPst = caLineSplits ? Object.values(caLineSplits).reduce((s, p) => s + p.nonRecoverable, 0) : 0;
  // Assigned total as JobTread will record it (PST is part of the cost).
  $: displayTotalAssigned = totalAssigned + (caLineSplits && !useSplitMode
    ? assignedItems.reduce((s, it) => s + (caLineSplits[it.invoiceLineItem.lineItemID]?.nonRecoverable || 0), 0)
    : (caLineSplits && useSplitMode && Number(currentBill?.subTotal) ? totalAssigned * caBillPst / Number(currentBill.subTotal) : 0));

  // Variables for customer search autocomplete
  let customerSearchQuery = '';
  let showCustomerDropdown = false;
  let filteredCustomers = [];
  let customerInputElement = null; // Reference to customer input for auto-focus
  let vendorOrderInputElement = null; // Reference to vendor order input for auto-focus

  // Variables for vendor order search autocomplete
  let vendorOrderSearchQuery = '';
  let showVendorOrderDropdown = false;
  let filteredVendorOrders = [];

  // Variables for cost group search autocomplete (Job mode) - per line item
  let lineItemCostGroupSearchQueries = {}; // Map of lineItemID to search query
  let lineItemCostGroupDropdowns = {}; // Map of lineItemID to dropdown visibility
  let jobCostGroups = []; // Cost groups for selected jobs
  let lineItemFilteredCostGroups = {}; // Map of lineItemID to filtered cost groups

  let isLoadingCostGroups = false;
  let costGroupsCached = false; // Track if data is from cache
  let costGroupsCachedAt = null; // When cache was created
  // Retained for backward compat with other references in this file —
  // forced ON so it never re-filters out optional/selectable budget lines.
  let showUnapprovedBudgetItems = true;

  // Active lens for the assignment search dropdown ('costGroup' or 'costCode').
  // Initial value comes from the user's saved default in onMount; can be
  // flipped per session via the toggle in the table header.
  let activeLens = 'costGroup';

  // Assignment dropdown variables
  let assignmentSearchQueries = {}; // Map of lineItemID to search text
  let assignmentDropdownOpen = {}; // Map of lineItemID to boolean (is dropdown visible)
  let assignmentDropdownPositions = {}; // Map of lineItemID to {top, left, width}

  // Split mode variables
  let useSplitMode = false; // Toggle state - defaults to "Use Lines from Xero"
  let selectedCostGroupForSplit = null; // Selected cost group for splitting
  let splitAssignments = []; // Calculated split assignments
  let expandedCostGroups = {}; // Track which cost groups are expanded
  
  // Amount selection variables (for partial payments)
  let useAmountDue = true; // Default to amount due when available

  // New variables for tracking multiple vendor bill creation
  let processingVendorBills = false;
  let vendorBillsProcessed = 0;
  let totalVendorBillsToProcess = 0;
  let vendorBillsCreationResults = [];

  // New variables for error handling
  let creationError = null;
  let showErrorModal = false;

  let jobtreadConfig = null;

  // Cost code mapping variables for smart auto-assignment
  let costCodeMappings = {};        // JT code -> Xero code
  let reverseCostCodeMappings = {}; // Xero code -> [JT codes]

  /**
   * Fetch JobTread user and organization info for document creation
   */
  async function fetchJobTreadUserInfo() {
    try {
      const response = await fetch('/api/jt/user-info');
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          jobtreadUserName = data.userName || "BuildZilla";
          jobtreadOrgName = data.organizationName || "BuildZilla";
          console.log('📋 JobTread user info loaded:', { user: jobtreadUserName, org: jobtreadOrgName });
        }
      }
    } catch (error) {
      console.error('Error fetching JobTread user info:', error);
      // Keep default values
    }
  }

  /**
   * Load cost code mappings from user settings and build reverse lookup
   */
  async function loadCostCodeMappings() {
    try {
      const response = await fetch('/api/users/cost-code-mappings');
      const data = await response.json();
      if (data.success) {
        costCodeMappings = data.mappings || {};
        reverseCostCodeMappings = buildReverseMappings(costCodeMappings);
        console.log('📋 Loaded cost code mappings:', Object.keys(costCodeMappings).length, 'mappings');
      }
    } catch (err) {
      console.warn('Failed to load cost code mappings:', err);
    }
  }

  /**
   * Build reverse mapping: Xero account code -> [JT cost code numbers]
   * Handles both plain strings and DynamoDB AttributeValue format { "S": "value" }
   */
  function buildReverseMappings(mappings) {
    const reverse = {};
    for (const [jtCode, rawXeroCode] of Object.entries(mappings)) {
      // Handle DynamoDB AttributeValue format { "S": "value" } if present
      const xeroCode = (typeof rawXeroCode === 'object' && rawXeroCode?.S) 
        ? rawXeroCode.S 
        : rawXeroCode;
      
      if (!reverse[xeroCode]) {
        reverse[xeroCode] = [];
      }
      reverse[xeroCode].push(jtCode);
    }
    return reverse;
  }

  onMount(async () => {
    // Fetch user data and set tax configuration first
    try {
      const userResponse = await fetch('/api/user');
      if (userResponse.ok) {
        const userData = await userResponse.json();
        if (userData.success && userData.user) {
          userCountry = userData.user.country;
          userProvince = userCountry === 'CA' ? normalizeProvince(userData.user.province) : null;
          
          // Set tax configuration based on country
          switch (userCountry) {
            case 'NZ':
              taxConfig = { rate: 0.15, name: "GST", currency: "NZD", locale: "en-NZ" };
              break;
            case 'AU':
              taxConfig = { rate: 0.10, name: "GST", currency: "AUD", locale: "en-AU" };
              break;
            case 'US':
              taxConfig = { rate: 0.00, name: "Sales Tax", currency: "USD", locale: "en-US" };
              break;
            case 'GB':
              taxConfig = { rate: 0.00, name: "VAT", currency: "GBP", locale: "en-GB" };
              break;
            case 'GI':
              taxConfig = { rate: 0.00, name: "VAT", currency: "GIP", locale: "en-GI" };
              break;
            case 'CA':
              // Fallback only - replaced per bill from the bill's own tax (see loadCurrentBill).
              taxConfig = { rate: 0.05, name: userProvince ? provinceBillTaxName(userProvince) : "GST/HST", currency: "CAD", locale: "en-CA" };
              caBaseTaxConfig = taxConfig;
              fetch('/api/xero/non-recoverable-rates')
                .then(r => (r.ok ? r.json() : null))
                .then(d => { if (d?.nonRecoverableByTaxType) caNonRecoverableByTaxType = d.nonRecoverableByTaxType; })
                .catch(() => {});
              break;
            default:
              taxConfig = { rate: 0.15, name: "GST", currency: "NZD", locale: "en-NZ" }; // Default to NZ
          }
          
          // Load bill mode default from user settings
          if (userData.user.billSettings) {
            isJobMode = userData.user.billSettings.billmodedefaultjob || false;
            previousMode = isJobMode; // Initialize previousMode to match
            billTrackingSettings = userData.user.billSettings;
            console.log('Bill mode setting loaded:', isJobMode ? 'Job Mode' : 'PO Mode');
          }

          // Load assignment lens default ('costGroup' | 'costCode')
          if (userData.user.assignmentLensDefault === 'costCode') {
            activeLens = 'costCode';
            console.log('Assignment lens default loaded: Cost Code');
          } else {
            activeLens = 'costGroup';
            console.log('Assignment lens default loaded: Cost Group');
          }
          
          console.log('User country:', userCountry, 'Tax config:', taxConfig);
        }
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      // Use default tax config if fetch fails
    }

    // Fetch JobTread user/org info for personalized document creation
    await fetchJobTreadUserInfo();

    // Load cost code mappings for smart auto-assignment in Job mode
    await loadCostCodeMappings();

    // Fetch customers (jobs) first - required for line-item matching
    try {
      await fetchJobs();

      console.log('Location state:', $location.state);
      if ($location.state && $location.state.selectedBills) {
        selectedBills = $location.state.selectedBills;
        currentBillIndex = 0;
        currentBill = null;
        error = null;
        selectedCustomer = null;
        selectedVendorOrder = null;
        vendorOrders = [];
        lineItemsData = null;
        allItemsAssigned = false;
        isAutoMatched = false;
        vendorBillCreated = false;
        assignedItems = [];
        showVendorMatching = false;
        pendingVendorCandidates = [];
        pendingVendorSourceName = '';
        jtVendors = [];
        filteredVendors = [];
        vendorSearchQuery = "";
        selectedVendorId = null;
        console.log('Received bills:', selectedBills);
        loadCurrentBill();
      } else {
        error = 'No bills selected';
        console.error('No selected bills found in location state');
      }
      
      // After jobs are loaded, auto-focus the customer input and show dropdown
      setTimeout(() => {
        if (customerInputElement) {
          customerInputElement.focus();
          showCustomerDropdown = true;
          filteredCustomers = customers; // Show all customers in dropdown
        }
      }, 100); // Small delay to ensure DOM is ready
    } catch (error) {
      console.error('Error fetching customers:', error);
    }

    try {
      const response = await fetch('/api/jt/check-auth');
      const data = await response.json();
      console.log('JobTread auth check response:', data);
      
      // SECURITY: Server no longer sends grantKey - only hasGrantKey boolean
      if (data.authenticated && data.hasGrantKey && data.orgId) {
        jobtreadConfig = {
          orgId: data.orgId,
          authenticated: true
        };
        console.log('JobTread config set successfully:', { 
          authenticated: jobtreadConfig.authenticated, 
          hasOrgId: !!jobtreadConfig.orgId 
        });
      } else {
        console.error('Invalid auth response:', data);
        throw new Error('Not authenticated with JobTread or missing credentials');
      }
    } catch (error) {
      console.error('Error fetching JobTread config:', error);
    }
    
    // Add scroll and resize listeners to recalculate dropdown positions
    const handleScroll = () => recalculateDropdownPositions();
    const handleResize = () => recalculateDropdownPositions();
    
    window.addEventListener('scroll', handleScroll, true); // Use capture phase
    window.addEventListener('resize', handleResize);
    
    // Cleanup on unmount
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  });

  async function loadCurrentBill() {
    if (currentBillIndex < selectedBills.length) {
      currentBill = selectedBills[currentBillIndex];
      if (userCountry === 'CA' && caBaseTaxConfig) {
        const own = canadaTaxConfigForXeroBill(currentBill, userProvince);
        taxConfig = own ? { ...caBaseTaxConfig, ...own } : caBaseTaxConfig;
      }
      console.log('Loaded bill:', currentBill);
      
      // Reset JobTread selections
      selectedCustomer = null;
      selectedVendorOrder = null;
      vendorOrders = [];
      lineItemsData = null;
      allItemsAssigned = false;
      isAutoMatched = false;
      vendorBillCreated = false;
      assignedItems = [];
      multipleMatchedPOs = false;
      matchedPOCount = { found: 0, total: 0 };
      
      // Reset new multiple vendor bill variables
      matchedVendorBills = [];
      lineItemVendorBillSelections = {};
      
      // Reset split mode and amount selection
      useSplitMode = false;
      selectedCostGroupForSplit = null;
      splitAssignments = [];
      expandedCostGroups = {};
      useAmountDue = true; // Default to amount due for new bills

      if (!isJobMode && currentBill.lineItems && currentBill.lineItems.length > 0 && customers.length > 0) {
        await runPOModeAutoMatch();
      } else if (isJobMode && currentBill.lineItems) {
        await tryAutoMatchJobFromTrackingCategory();
      }
    } else {
      console.log('All bills processed');
      navigate('/import-bills-from-xero');
    }
  }

  function nextBill() {
    currentBillIndex++;
    
    // Reset vendor matching state for next bill
    showVendorMatching = false;
    autoMatchedVendor = false;
    autoMatchFailed = false;
    isAutoMatchingVendor = false;
    pendingVendorCandidates = [];
    pendingVendorSourceName = '';
    selectedVendorId = null;
    vendorSearchQuery = "";
    vendorBillCreated = false;
    
    loadCurrentBill();
  }

  async function handleCustomerSelect(event) {
    selectedCustomer = event.target.value;
    selectedVendorOrder = null; // Reset vendor order selection
    lineItemsData = null; // Reset line items data
    isAutoMatched = false; // Reset auto-match status since user manually selected
    vendorOrders = []; // Reset vendor orders array
    
    if (selectedCustomer) {
      isLoadingVendorOrders = true;
      try {
        let nextPage = null;
        let allOrders = [];
        
        do {
          // Fetch vendor orders with pagination
          const url = `/api/jt/documents?jtjobid=${selectedCustomer}${nextPage ? `&page=${nextPage}` : ''}`;
          console.log('Fetching vendor orders from:', url);
          
          const response = await fetch(url);
          if (!response.ok) {
            throw new Error('Failed to fetch vendor orders');
          }
          
          const data = await response.json();
          console.log('Vendor orders page response:', data);
          
          // Check if we have documents in the response
          if (Array.isArray(data)) {
            allOrders = [...allOrders, ...data];
            // If data is an array, assume no more pages
            nextPage = null;
          } else if (data.orders && Array.isArray(data.orders)) {
            // Handle paginated response format
            allOrders = [...allOrders, ...data.orders];
            nextPage = data.nextPage;
          } else {
            console.error('Unexpected response format:', data);
            break;
          }
          
          console.log(`Fetched ${allOrders.length} orders so far, next page: ${nextPage}`);
        } while (nextPage);
        
        vendorOrders = allOrders;
        console.log('All vendor orders fetched:', vendorOrders.length);
        
      } catch (error) {
        console.error('Error fetching vendor orders:', error);
      } finally {
        isLoadingVendorOrders = false;
      }
    }
  }

  // Add functions to handle dropdown focus management
  function handleInputFocusOut(type) {
    // Use a small delay to allow click events to process first
    setTimeout(() => {
      if (type === 'customer') {
        showCustomerDropdown = false;
      } else if (type === 'vendorOrder') {
        showVendorOrderDropdown = false;
      }
    }, 200);
  }

  async function selectVendorOrder(orderId, toggleDropdown = true) {
    console.log('Selecting vendor order:', orderId);
    selectedVendorOrder = orderId;
    isLoadingCostGroups = true;
    
    if (toggleDropdown) {
      showVendorOrderDropdown = false;
    }
    
    try {
        const response = await fetch(`/api/jt/lineitems?jtjobid=${orderId}`);
        if (!response.ok) {
            throw new Error('Failed to fetch line items');
        }
        lineItemsData = await response.json();
        console.log('Fetched lineItemsData:', lineItemsData);
        console.log('🔍 LineItemsData structure:', {
          hasCostGroups: !!lineItemsData?.costGroups,
          costGroupCount: lineItemsData?.costGroups?.length || 0,
          totalItems: lineItemsData?.costGroups?.reduce((sum, g) => sum + (g.lineItems?.length || 0), 0) || 0,
          firstGroupSample: lineItemsData?.costGroups?.[0] ? {
            id: lineItemsData.costGroups[0].id,
            name: lineItemsData.costGroups[0].name,
            itemCount: lineItemsData.costGroups[0].lineItems?.length
          } : null
        });
        
        // Verify the data structure
        if (!lineItemsData || !lineItemsData.costGroups) {
            console.error('Invalid lineItemsData structure:', lineItemsData);
            throw new Error('Invalid line items data received');
        }
        
        // Auto-expand first assignment dropdown after cost groups are loaded
        autoExpandFirstAssignmentDropdown();
    } catch (error) {
        console.error('Error fetching line items:', error);
        lineItemsData = null; // Reset on error
    } finally {
        isLoadingCostGroups = false;
    }
  }

  // Helper function to get validation class for assignment dropdowns
  function getAssignmentValidationClass(lineItem) {
    // Check if cost groups are loaded
    const costGroupsLoaded = lineItemsData?.costGroups && lineItemsData.costGroups.length > 0;
    
    // Check if this line item has been assigned
    const isAssigned = assignedItems.some(item => item.invoiceLineItem.lineItemID === lineItem.lineItemID);
    
    // If cost groups are loaded but line item is not assigned, show orange border
    if (costGroupsLoaded && !isAssigned) {
      return 'border-2 border-orange-500';
    }
    
    return 'border border-gray-300';
  }

  // Helper function to get the current value for assignment dropdown (Column 1 - cost groups)
  function getAssignmentValue(lineItem, assignmentArray) {
    const assigned = assignmentArray.find(item => 
      item.invoiceLineItem.lineItemID === lineItem.lineItemID
    );
    
    if (!assigned || !assigned.costGroup || !assigned.originalItem) {
      return '';
    }
    
    // Find the group index and return the value in format "groupIndex|itemId"
    const groupIndex = lineItemsData?.costGroups?.findIndex(
      group => group.id === assigned.costGroup.id
    );
    
    if (groupIndex === -1) return '';
    
    return `${groupIndex}|${assigned.originalItem.id}`;
  }

  // Assignment dropdown search functions
  function handleAssignmentSearch(lineItemId, searchText) {
    console.log('Assignment search:', { lineItemId, searchText });
    assignmentSearchQueries = {
      ...assignmentSearchQueries,
      [lineItemId]: searchText
    };
    
    // Ensure dropdown stays open during search
    if (!assignmentDropdownOpen[lineItemId]) {
      assignmentDropdownOpen = {
        ...assignmentDropdownOpen,
        [lineItemId]: true
      };
    }
  }

  function getFilteredAssignmentItems(lineItemId) {
    const searchQuery = (assignmentSearchQueries[lineItemId] || '').trim();
    const lowerQuery = searchQuery.toLowerCase();

    let costGroups = [];
    if (isJobMode) {
      costGroups = lineItemsData?.costGroups || [];
    } else {
      const selectedBillId = lineItemVendorBillSelections[lineItemId];
      const selectedBill = matchedVendorBills.find(bill => bill.id === selectedBillId);
      costGroups = selectedBill?.lineItems?.costGroups || [];
    }

    console.log('Filtering assignment items:', {
      lineItemId,
      searchQuery,
      totalGroups: costGroups.length,
      totalItems: costGroups.reduce((sum, g) => sum + (g.lineItems?.length || 0), 0)
    });

    // Sort key for tree-order rendering: build [root, mid, leaf] from
    // grandparent down so siblings appear together. Numeric-aware compare
    // so "1-DESIGN" sorts before "10-PRELIM".
    const sortKey = (g) => {
      const gp = g.parentCostGroup?.parentCostGroup?.name || '';
      const p = g.parentCostGroup?.name || '';
      const own = g.name || '';
      if (gp) return [gp, p, own];
      if (p) return [p, own, ''];
      return [own, '', ''];
    };
    const compareTuples = (a, b) => {
      for (let i = 0; i < 3; i++) {
        const cmp = a[i].localeCompare(b[i], undefined, { numeric: true, sensitivity: 'base' });
        if (cmp !== 0) return cmp;
      }
      return 0;
    };
    const sortByTree = (arr) => [...arr].sort((a, b) => compareTuples(sortKey(a), sortKey(b)));

    // No isSelected/"approval" filtering — the server already restricts to
    // true budget items (document.id = null). JobTread's isSelected flag
    // marks pre-selected vs. selectable/optional items; selectable items
    // (e.g. things under "Prime cost, Provisional Cost and other selectable
    // items") are still legitimate budget targets.

    if (!searchQuery) {
      return sortByTree(costGroups);
    }

    // Search matches the item name + group name + parent + grandparent so
    // typing a top-level category surfaces every descendant.
    const filtered = costGroups.map(group => {
      const groupName = (cleanCostGroupName(group.name) || '').toLowerCase();
      const parentName = (cleanCostGroupName(group.parentCostGroup?.name) || '').toLowerCase();
      const grandparentName = (cleanCostGroupName(group.parentCostGroup?.parentCostGroup?.name) || '').toLowerCase();
      const groupHierarchyMatches =
        groupName.includes(lowerQuery) ||
        parentName.includes(lowerQuery) ||
        grandparentName.includes(lowerQuery);

      if (groupHierarchyMatches) {
        return { ...group, lineItems: group.lineItems || [] };
      } else {
        const matchingItems = (group.lineItems || []).filter(item => {
          const itemName = item.name?.toLowerCase() || '';
          const costCodeNumber = item.costCode?.number?.toString().toLowerCase() || '';
          const costCodeName = item.costCode?.name?.toLowerCase() || '';
          return itemName.includes(lowerQuery)
            || costCodeNumber.includes(lowerQuery)
            || costCodeName.includes(lowerQuery);
        });
        return { ...group, lineItems: matchingItems };
      }
    }).filter(group => group.lineItems.length > 0);

    console.log('Filtered results:', {
      matchingGroups: filtered.length,
      matchingItems: filtered.reduce((sum, g) => sum + g.lineItems.length, 0)
    });

    return sortByTree(filtered);
  }

  // Cost-code lens: returns items regrouped by their parent cost code.
  // Same shape as getFilteredAssignmentItems so selectAssignmentItem() works
  // unchanged — each item smuggles _originalGroupIndex.
  function getFilteredAssignmentItemsByCode(lineItemId) {
    const searchQuery = (assignmentSearchQueries[lineItemId] || '').trim();
    const lowerQuery = searchQuery.toLowerCase();

    let costGroups = [];
    if (isJobMode) {
      costGroups = lineItemsData?.costGroups || [];
    } else {
      const selectedBillId = lineItemVendorBillSelections[lineItemId];
      const selectedBill = matchedVendorBills.find(bill => bill.id === selectedBillId);
      costGroups = selectedBill?.lineItems?.costGroups || [];
    }

    const scoped = costGroups;

    const flat = [];
    scoped.forEach(group => {
      const originalGroupIndex = costGroups.findIndex(g => g.id === group.id);
      (group.lineItems || []).forEach(item => {
        flat.push({ item, originalGroupIndex, costGroup: group });
      });
    });

    const filtered = !searchQuery ? flat : flat.filter(({ item }) => {
      const itemName = item.name?.toLowerCase() || '';
      const codeNum = item.costCode?.number?.toString().toLowerCase() || '';
      const codeName = item.costCode?.name?.toLowerCase() || '';
      const parentCodeNum = item.costCode?.parentCostCode?.number?.toString().toLowerCase() || '';
      const parentCodeName = item.costCode?.parentCostCode?.name?.toLowerCase() || '';
      return itemName.includes(lowerQuery)
        || codeNum.includes(lowerQuery)
        || codeName.includes(lowerQuery)
        || parentCodeNum.includes(lowerQuery)
        || parentCodeName.includes(lowerQuery);
    });

    const bucketsByParent = new Map();
    const NO_CODE_KEY = '__no_code__';
    for (const entry of filtered) {
      const parent = entry.item.costCode?.parentCostCode;
      const key = parent?.id || (entry.item.costCode?.id || NO_CODE_KEY);
      if (!bucketsByParent.has(key)) {
        bucketsByParent.set(key, {
          id: key,
          name: parent
            ? `${parent.number || ''}  ${parent.name || ''}`.trim()
            : (entry.item.costCode
                ? `${entry.item.costCode.number || ''}  ${entry.item.costCode.name || ''}`.trim()
                : 'Uncategorised'),
          parentCostGroup: null,
          lineItems: []
        });
      }
      const bucket = bucketsByParent.get(key);
      bucket.lineItems.push({
        ...entry.item,
        _originalGroupIndex: entry.originalGroupIndex,
        costGroup: entry.costGroup
          ? {
              id: entry.costGroup.id,
              name: entry.costGroup.name,
              parentCostGroup: entry.costGroup.parentCostGroup || null
            }
          : null
      });
    }

    return Array.from(bucketsByParent.values()).sort((a, b) => {
      const ax = parseInt((a.name.match(/^\d+/) || ['9999'])[0], 10);
      const bx = parseInt((b.name.match(/^\d+/) || ['9999'])[0], 10);
      return ax - bx;
    });
  }

  function getAssignmentBucketsByActiveLens(lineItemId) {
    return activeLens === 'costCode'
      ? getFilteredAssignmentItemsByCode(lineItemId)
      : getFilteredAssignmentItems(lineItemId);
  }

  function toggleAssignmentDropdown(lineItemId) {
    const isCurrentlyOpen = assignmentDropdownOpen[lineItemId];
    assignmentDropdownOpen = {
      ...assignmentDropdownOpen,
      [lineItemId]: !isCurrentlyOpen
    };
    
    if (!isCurrentlyOpen) {
      assignmentSearchQueries = {
        ...assignmentSearchQueries,
        [lineItemId]: ''
      };
    }
  }

  function openAssignmentDropdown(lineItemId, inputElement, options = {}) {
    const { preserveAssignment = false } = options;
    console.log('Opening assignment dropdown for lineItem:', lineItemId);
    
    // Calculate position if element is provided
    if (inputElement) {
      const rect = inputElement.getBoundingClientRect();
      assignmentDropdownPositions = {
        ...assignmentDropdownPositions,
        [lineItemId]: {
          top: rect.bottom,
          left: rect.left,
          width: rect.width,
          inputElement // Store reference to recalculate later
        }
      };
    }
    
    assignmentDropdownOpen = {
      ...assignmentDropdownOpen,
      [lineItemId]: true
    };
    
    // Clear the search query so the full list is shown (unless preserving assignment)
    if (!preserveAssignment) {
      assignmentSearchQueries = {
        ...assignmentSearchQueries,
        [lineItemId]: ''
      };
    }
    
    // Only clear existing assignment when user manually opens dropdown (e.g. to change selection)
    // Do NOT clear when auto-expanding after auto-assignment, or we wipe the selections we just made
    if (!preserveAssignment) {
      const existingIndex = assignedItems.findIndex(item => 
        item.invoiceLineItem.lineItemID === lineItemId
      );
      if (existingIndex !== -1) {
        assignedItems = [
          ...assignedItems.slice(0, existingIndex),
          ...assignedItems.slice(existingIndex + 1)
        ];
        console.log('Cleared existing assignment for lineItem:', lineItemId);
        checkAllItemsAssigned();
      }
    }
    
    console.log('Dropdown state:', assignmentDropdownOpen);
    console.log('Has costGroups:', isJobMode ? lineItemsData?.costGroups?.length : matchedVendorBills.find(bill => bill.id === lineItemVendorBillSelections[lineItemId])?.lineItems?.costGroups?.length);
  }
  
  // Function to recalculate all open dropdown positions
  function recalculateDropdownPositions() {
    const openDropdowns = Object.entries(assignmentDropdownOpen).filter(([_, isOpen]) => isOpen);
    if (openDropdowns.length === 0) return;
    
    const newPositions = { ...assignmentDropdownPositions };
    for (const [lineItemId] of openDropdowns) {
      const position = newPositions[lineItemId];
      if (position?.inputElement) {
        const rect = position.inputElement.getBoundingClientRect();
        newPositions[lineItemId] = {
          ...position,
          top: rect.bottom,
          left: rect.left,
          width: rect.width
        };
      }
    }
    assignmentDropdownPositions = newPositions;
  }
  
  // Recalculate dropdown positions when layout-affecting variables change
  $: if (isLoadingCostGroups || matchedVendorBills || showInlineAddForm) {
    // Use setTimeout to recalculate after DOM updates
    setTimeout(recalculateDropdownPositions, 0);
  }

  function selectAssignmentItem(lineItem, groupIndex, itemId, isAutoAssign = false) {
    console.log('selectAssignmentItem called with:', { lineItem, groupIndex, itemId, isJobMode, isAutoAssign });
    
    // Find the selected item to get its name
    let costGroups = [];
    if (isJobMode) {
      costGroups = lineItemsData?.costGroups || [];
    } else {
      const selectedBillId = lineItemVendorBillSelections[lineItem.lineItemID];
      const selectedBill = matchedVendorBills.find(bill => bill.id === selectedBillId);
      costGroups = selectedBill?.lineItems?.costGroups || [];
    }
    
    const selectedGroup = costGroups[groupIndex];
    const selectedItem = selectedGroup?.lineItems?.find(item => item.id === itemId);
    
    // Set search query to the selected item's display text
    if (selectedItem) {
      let displayText = selectedItem.name || '';
      if (selectedItem.costCode) {
        displayText += ` (${selectedItem.costCode.number} ${selectedItem.costCode.name})`;
      }
      assignmentSearchQueries = {
        ...assignmentSearchQueries,
        [lineItem.lineItemID]: displayText
      };
    }
    
    // Close the dropdown
    assignmentDropdownOpen = {
      ...assignmentDropdownOpen,
      [lineItem.lineItemID]: false
    };
    
    // Call the existing assignment logic
    const mockEvent = {
      target: {
        value: `${groupIndex}|${itemId}`
      }
    };
    handleAssignmentSelect(lineItem, mockEvent);
    
    // Check for fill-down on manual selections in Job mode
    if (isJobMode && !isAutoAssign) {
      tryFillDownAssignment(lineItem, groupIndex, itemId);
    }
  }

  /**
   * Fill down the same assignment to all remaining blank rows
   * Only triggers when: first row is being assigned AND all remaining rows are blank
   */
  function tryFillDownAssignment(sourceLineItem, groupIndex, itemId) {
    if (!currentBill?.lineItems) return;
    
    const sourceIndex = currentBill.lineItems.findIndex(
      li => li.lineItemID === sourceLineItem.lineItemID
    );
    
    if (sourceIndex === -1) return;
    
    // Check if there are any assignments BEFORE this row
    const hasEarlierAssignments = currentBill.lineItems.slice(0, sourceIndex).some(li =>
      assignedItems.find(a => a.invoiceLineItem.lineItemID === li.lineItemID)
    );
    
    if (hasEarlierAssignments) {
      // Not the first assignment, don't fill down
      return;
    }
    
    // Check if all remaining rows are blank
    const remainingItems = currentBill.lineItems.slice(sourceIndex + 1);
    const allRemainingBlank = remainingItems.every(li =>
      !assignedItems.find(a => a.invoiceLineItem.lineItemID === li.lineItemID)
    );
    
    if (!allRemainingBlank || remainingItems.length === 0) {
      return;
    }
    
    console.log(`📋 Fill-down: Applying same assignment to ${remainingItems.length} remaining rows`);
    
    // Fill down to all remaining rows (pass true to prevent recursive fill-down)
    for (const li of remainingItems) {
      selectAssignmentItem(li, groupIndex, itemId, true);
    }
  }

  function handleAssignmentInputFocusOut(lineItemId) {
    setTimeout(() => {
      assignmentDropdownOpen = {
        ...assignmentDropdownOpen,
        [lineItemId]: false
      };
    }, 200);
  }

  function autoExpandFirstAssignmentDropdown() {
    // Skip if all line items are already assigned (e.g. after auto-assignment)
    if (assignedItems.length >= (currentBill?.lineItems?.length ?? 0) && assignedItems.length > 0) {
      console.log('Skipping auto-expand: all items already assigned');
      return;
    }

    // Use a retry mechanism to handle DOM changes
    let attempts = 0;
    const maxAttempts = 5;

    const tryExpand = () => {
      attempts++;
      console.log(`Auto-expand attempt ${attempts}/${maxAttempts}`);

      if (currentBill?.lineItems && currentBill.lineItems.length > 0) {
        const firstLineItem = currentBill.lineItems[0];
        const firstLineItemId = firstLineItem.lineItemID;
        
        console.log('Auto-expanding first assignment dropdown for lineItem:', firstLineItemId);
        
        // Check if line item has a vendor bill/job selection (required for input to be enabled)
        if (!lineItemVendorBillSelections[firstLineItemId]) {
          console.warn('First line item has no vendor bill/job selected, retrying...');
          if (attempts < maxAttempts) {
            setTimeout(tryExpand, 300);
          }
          return;
        }
        
        // Check if cost groups are loaded
        const hasCostGroups = isJobMode ? 
          (lineItemsData?.costGroups && lineItemsData.costGroups.length > 0) :
          (matchedVendorBills.find(bill => bill.id === lineItemVendorBillSelections[firstLineItemId])?.lineItems?.costGroups);
        
        if (!hasCostGroups) {
          console.warn('No cost groups loaded, retrying...');
          if (attempts < maxAttempts) {
            setTimeout(tryExpand, 300);
          }
          return;
        }
        
        // Find the input element for the first line item's assignment field
        // Try multiple selectors to find the input
        const inputElement = document.querySelector(`input[id*="assignment-${firstLineItemId}"]`) ||
                           document.querySelector(`input[placeholder*="Search to assign"]:not([disabled])`);
        
        if (inputElement && !inputElement.disabled) {
          console.log('✅ Found enabled input element, opening dropdown');
          
          // Open the dropdown (preserve assignments - we may have just auto-assigned)
          openAssignmentDropdown(firstLineItemId, inputElement, { preserveAssignment: true });
          
          // Focus the input with a slight delay
          setTimeout(() => {
            inputElement.focus();
            console.log('✅ Successfully auto-expanded first assignment dropdown');
          }, 50);
        } else {
          console.warn('Could not find enabled input element, retrying...', {
            inputElement: !!inputElement,
            disabled: inputElement?.disabled,
            attemptRemaining: maxAttempts - attempts
          });
          if (attempts < maxAttempts) {
            setTimeout(tryExpand, 300);
          }
        }
      }
    };
    
    // Start the first attempt after initial delay
    setTimeout(tryExpand, 500);
  }

  function getAssignmentDisplayText(lineItem) {
    const assigned = assignedItems.find(item => 
      item.invoiceLineItem.lineItemID === lineItem.lineItemID
    );
    
    if (!assigned || !assigned.originalItem) {
      return '';
    }
    
    const item = assigned.originalItem;
    let displayText = item.name || '';
    
    if (item.costCode) {
      displayText += ` (${item.costCode.number} ${item.costCode.name})`;
    }
    
    return displayText;
  }

  function handleAssignmentSelect(lineItem, event) {
    console.log('handleAssignmentSelect called with:', { lineItem, eventValue: event.target.value, isJobMode });
    
    if (!event.target.value) {
        console.log('No value selected - clearing assignment');
        // Remove from assignedItems if exists (clearing the selection)
        const existingIndex = assignedItems.findIndex(item => 
          item.invoiceLineItem.lineItemID === lineItem.lineItemID
        );
        if (existingIndex !== -1) {
          assignedItems = [
            ...assignedItems.slice(0, existingIndex),
            ...assignedItems.slice(existingIndex + 1)
          ];
          console.log('Cleared assignment');
          checkAllItemsAssigned();
        }
        return;
    }

    const [groupIndex, lineItemId] = event.target.value.split('|');
    
    if (isJobMode) {
        // Job Mode: Use lineItemsData (job cost groups)
        console.log('Job Mode assignment - using lineItemsData');
        
        if (!lineItemsData?.costGroups) {
            console.error('No job cost groups data available');
            return;
        }
        
        // Get the selected cost group from job data
        const selectedGroup = lineItemsData.costGroups[groupIndex];
        if (!selectedGroup) {
            console.error('Selected cost group not found in job data:', { groupIndex });
            return;
        }
        
        // Find the specific cost item in the selected group
        const originalItem = selectedGroup.lineItems.find(item => item.id === lineItemId);
        if (!originalItem) {
            console.error('Could not find cost item in selected group:', { lineItemId, groupName: selectedGroup.name });
            return;
        }

        // Calculate total amount (quantity × unit amount)
        const totalAmount = (lineItem.quantity || 1) * lineItem.unitAmount;

        // Create the new assigned item for Job Mode
        const newAssignedItem = {
            invoiceLineItem: {
                ...lineItem,
                assignedCost: totalAmount || 0
            },
            originalItem: originalItem,
            costGroup: selectedGroup, // Store the cost group for Job mode
            vendorBillId: 'job-mode', // Mark as Job mode assignment
            mode: 'job' // Explicitly mark the assignment mode
        };
        
        // Find if this line item is already assigned
        const existingIndex = assignedItems.findIndex(item => 
            item.invoiceLineItem.lineItemID === lineItem.lineItemID
        );
        
        if (existingIndex !== -1) {
            // Replace existing assignment
            assignedItems = [
                ...assignedItems.slice(0, existingIndex),
                newAssignedItem,
                ...assignedItems.slice(existingIndex + 1)
            ];
        } else {
            // Add new assignment
            assignedItems = [...assignedItems, newAssignedItem];
        }
        
        console.log('Job mode: Updated assignedItems:', assignedItems);
        checkAllItemsAssigned();
        return;
    }
    
    // PO Mode: Original logic using vendor bills
    console.log('PO Mode assignment - using vendor bills');
    
    // Get the selected vendor bill for this line item
    const selectedBillId = lineItemVendorBillSelections[lineItem.lineItemID];
    if (!selectedBillId) {
        console.error('No vendor bill selected for this line item');
        return;
    }
    
    // Find the selected vendor bill
    const selectedBill = matchedVendorBills.find(bill => bill.id === selectedBillId);
    if (!selectedBill || !selectedBill.lineItems) {
        console.error('Selected vendor bill not found or has no line items');
        return;
    }
    
    console.log('DEBUG: Selected vendor bill and line items:', {
        billId: selectedBillId,
        vendorOrderNumber: selectedBill.vendorOrderNumber,
        hasLineItems: !!selectedBill.lineItems,
        costGroupsCount: selectedBill.lineItems?.costGroups?.length || 0
    });
    
    // Get the line items data from the selected vendor bill
    const selectedBillLineItemsData = selectedBill.lineItems;
    
    // Make sure the selected bill's line items data is valid
    if (!selectedBillLineItemsData || !selectedBillLineItemsData.costGroups) {
        console.error('Invalid line items data structure for selected vendor bill:', selectedBillLineItemsData);
        return;
    }
    
    // Get the selected cost group from the selected bill's line items
    const selectedGroup = selectedBillLineItemsData.costGroups[groupIndex];
    if (!selectedGroup) {
        console.error('Selected cost group not found in vendor bill:', { groupIndex, billId: selectedBillId });
        return;
    }
    
    // Find the specific line item in the selected bill
    const originalItem = selectedGroup.lineItems.find(item => item.id === lineItemId);
    if (!originalItem) {
        console.error('Could not find line item in selected vendor bill:', { lineItemId, billId: selectedBillId });
        return;
    }

    // Calculate total amount (quantity × unit amount)
    const totalAmount = (lineItem.quantity || 1) * lineItem.unitAmount;

    // Create the new assigned item
    const newAssignedItem = {
        invoiceLineItem: {
            ...lineItem,
            assignedCost: totalAmount || 0
        },
        originalItem: originalItem,
        vendorBillId: selectedBillId // Store the vendor bill ID for this assignment
    };

    // Find if this line item is already assigned
    const existingIndex = assignedItems.findIndex(item => 
        item.invoiceLineItem.lineItemID === lineItem.lineItemID
    );

    if (existingIndex !== -1) {
        // Replace existing assignment
        assignedItems = [
            ...assignedItems.slice(0, existingIndex),
            newAssignedItem,
            ...assignedItems.slice(existingIndex + 1)
        ];
    } else {
        // Add new assignment
        assignedItems = [...assignedItems, newAssignedItem];
    }

    console.log('Updated assignedItems:', assignedItems);
    checkAllItemsAssigned();
  }

  function calculateTotalAssigned() {
    if (useSplitMode) {
      // In split mode, total is always the invoice total (or sum of split assignments)
      totalAssigned = splitAssignments.reduce((sum, item) => {
        const amount = item.invoiceLineItem?.assignedCost || 0;
        return sum + amount;
      }, 0);
      console.log('Total assigned (Split Mode):', totalAssigned);
    } else {
      // Normal mode
      console.log('Calculating total from assignedItems:', assignedItems);
      
      totalAssigned = assignedItems.reduce((sum, item) => {
        const amount = item.invoiceLineItem?.assignedCost || 0;
        console.log(`Adding amount: ${amount} from line item: ${item.invoiceLineItem.description}`);
        return sum + amount;
      }, 0);
      
      console.log('New total assigned:', totalAssigned);
    }
  }

  function checkAllItemsAssigned() {
    calculateTotalAssigned();
    
    // Check if we have assignments for all line items
    if (useSplitMode) {
      // In split mode: check if a cost group is selected
      allItemsAssigned = selectedCostGroupForSplit !== null;
      console.log('Checking all items assigned (Split Mode):', {
        selectedCostGroupForSplit: selectedCostGroupForSplit?.name,
        allItemsAssigned: allItemsAssigned
      });
    } else {
      // In normal mode: check if all line items are assigned
      allItemsAssigned = assignedItems.length === currentBill.lineItems.length;
      console.log('Checking all items assigned (Normal Mode):', {
        assignedItemsLength: assignedItems.length,
        lineItemsLength: currentBill.lineItems.length,
        allItemsAssigned: allItemsAssigned
      });
    }
  }

  // Split Mode Functions
  
  function getEffectiveInvoiceAmount() {
    // Get the amount to use based on user selection
    // If useAmountDue is true and amountDue exists and is valid, use it
    // Otherwise fall back to total
    if (useAmountDue && currentBill.amountDue !== undefined && currentBill.amountDue > 0) {
      return currentBill.amountDue;
    }
    return currentBill.total || 0;
  }
  
  function hasPartialPayments() {
    // Check if there are partial payments on this bill
    return currentBill.amountDue !== undefined && 
           currentBill.amountDue < currentBill.total &&
           currentBill.amountDue > 0;
  }
  
  function getAvailableCostGroups() {
    // Get cost groups based on current mode
    console.log('🔍 getAvailableCostGroups called:', {
      isJobMode,
      matchedBillsCount: matchedVendorBills.length,
      lineItemsDataGroups: lineItemsData?.costGroups?.length || 0
    });
    
    if (isJobMode) {
      const groups = lineItemsData?.costGroups || [];
      console.log('🔍 Job Mode - returning', groups.length, 'cost groups');
      return groups;
    } else {
      // PO mode: get cost groups from selected vendor bills
      // For simplicity, use the first matched bill that has line items
      const billWithLineItems = matchedVendorBills.find(bill => bill.lineItems?.costGroups);
      const groups = billWithLineItems?.lineItems?.costGroups || [];
      console.log('🔍 PO Mode - found bill with line items:', {
        billId: billWithLineItems?.id,
        hasCostGroups: !!billWithLineItems?.lineItems?.costGroups,
        costGroupCount: groups.length,
        totalItems: groups.reduce((sum, g) => sum + (g.lineItems?.length || 0), 0)
      });
      return groups;
    }
  }

  function handleCostGroupSelection(costGroup) {
    console.log('Cost group selected for split:', costGroup);
    selectedCostGroupForSplit = costGroup;
    
    // Auto-expand the selected cost group
    expandedCostGroups = {
      ...expandedCostGroups,
      [costGroup.id]: true
    };
    
    calculateSplitAssignments(costGroup);
    checkAllItemsAssigned();
  }

  function calculateSplitAssignments(costGroup) {
    if (!costGroup || !costGroup.lineItems || costGroup.lineItems.length === 0) {
      console.error('Invalid cost group for split calculation');
      splitAssignments = [];
      return;
    }

    // Use the effective invoice amount based on user selection
    let invoiceTotal = getEffectiveInvoiceAmount();
    
    if (invoiceTotal === 0) {
      console.warn('Invoice total is zero, cannot calculate split');
      splitAssignments = [];
      return;
    }

    // Taxed if any line carries tax (same test as the rest of the page, so an
    // exempt tax type like EXEMPTEXPENSES doesn't count as taxed).
    const billHasGST = currentBill.lineItems && currentBill.lineItems.some(lineItem => hasGST(lineItem));

    // Split Xero's own figures rather than re-deriving them from one rate:
    // currentBill.total is tax-inclusive, so the pre-tax share is
    // subTotal / total and the tax share totalTax / total. A bill mixing exempt
    // and taxed lines then splits to exactly Xero's subtotal + tax.
    const billTotal = Number(currentBill.total) || 0;
    const billSubTotal = Number(currentBill.subTotal);
    const billTotalTax = Number(currentBill.totalTax);
    const haveXeroTotals = billTotal !== 0 && Number.isFinite(billSubTotal) && Number.isFinite(billTotalTax);
    let exclusiveTotal = invoiceTotal;
    let splitTaxRatio = 0; // tax per $1 of pre-tax split amount
    if (billHasGST && haveXeroTotals) {
      exclusiveTotal = invoiceTotal * (billSubTotal / billTotal);
      splitTaxRatio = billSubTotal !== 0 ? billTotalTax / billSubTotal : 0;
    } else if (billHasGST && taxConfig.rate > 0) {
      exclusiveTotal = invoiceTotal / (1 + taxConfig.rate);
      splitTaxRatio = taxConfig.rate;
    }

    console.log('GST Calculation for split:', {
      invoiceTotal,
      billHasGST,
      lineAmountTypes: currentBill.lineAmountTypes,
      taxRate: taxConfig.rate,
      exclusiveTotal,
      lineItemsWithGST: currentBill.lineItems.map(li => ({
        desc: li.description,
        taxType: li.taxType,
        taxAmount: li.taxAmount
      }))
    });

    // Calculate total budget for this cost group
    const totalBudget = costGroup.lineItems.reduce((sum, item) => {
      return sum + (item.cost || 0);
    }, 0);

    if (totalBudget === 0) {
      console.warn('Cost group total budget is zero, cannot calculate split');
      splitAssignments = [];
      return;
    }

    console.log('Calculating split:', {
      exclusiveTotal,
      totalBudget,
      itemCount: costGroup.lineItems.length
    });

    // Calculate split for each item based on its ratio using the EXCLUSIVE total
    splitAssignments = costGroup.lineItems.map(item => {
      const ratio = (item.cost || 0) / totalBudget;
      const splitAmount = exclusiveTotal * ratio; // Split the exclusive amount

      console.log(`Item: ${item.name}, Budget: ${item.cost}, Ratio: ${ratio.toFixed(4)}, Split (exclusive): ${splitAmount.toFixed(2)}`);

      return {
        invoiceLineItem: {
          description: currentBill.reference || "Invoice total split",
          lineItemID: `split-${item.id}`,
          assignedCost: splitAmount, // This is the exclusive amount
          quantity: 1,
          unitAmount: splitAmount, // This is the exclusive amount
          taxAmount: billHasGST ? Math.round(splitAmount * splitTaxRatio * 100) / 100 : 0, // Share of Xero's tax
          taxType: billHasGST ? 'OUTPUT2' : 'NONE' // Set appropriate tax type
        },
        originalItem: item,
        costGroup: costGroup,
        vendorBillId: isJobMode ? 'job-mode' : (lineItemVendorBillSelections[Object.keys(lineItemVendorBillSelections)[0]] || 'unknown'),
        mode: isJobMode ? 'job' : 'po',
        isSplitAssignment: true,
        splitRatio: ratio,
        itemBudget: item.cost || 0,
        hasGST: billHasGST // Store whether this has GST for createVendorBill to use
      };
    });

    // Per-split tax is rounded to cents; put the rounding remainder on the
    // largest split so the splits add back to exactly the tax being split.
    if (billHasGST && splitAssignments.length) {
      const targetTax = Math.round(exclusiveTotal * splitTaxRatio * 100) / 100;
      const splitTax = splitAssignments.reduce((sum, a) => sum + a.invoiceLineItem.taxAmount, 0);
      const remainder = Math.round((targetTax - splitTax) * 100) / 100;
      if (remainder !== 0) {
        const largest = splitAssignments.reduce((a, b) => (Math.abs(b.invoiceLineItem.unitAmount) > Math.abs(a.invoiceLineItem.unitAmount) ? b : a));
        largest.invoiceLineItem.taxAmount = Math.round((largest.invoiceLineItem.taxAmount + remainder) * 100) / 100;
      }
    }

    console.log('Split assignments calculated with GST handling:', splitAssignments);
    
    // Update total assigned
    calculateTotalAssigned();
  }

  function handleSplitModeToggle() {
    console.log('Split mode toggled:', useSplitMode);
    
    if (useSplitMode) {
      // Switching TO split mode - clear normal assignments
      assignedItems = [];
      selectedCostGroupForSplit = null;
      splitAssignments = [];
      expandedCostGroups = {};
    } else {
      // Switching FROM split mode - clear split assignments
      selectedCostGroupForSplit = null;
      splitAssignments = [];
      expandedCostGroups = {};
    }
    
    checkAllItemsAssigned();
  }

  function toggleCostGroupExpansion(costGroupId) {
    expandedCostGroups = {
      ...expandedCostGroups,
      [costGroupId]: !expandedCostGroups[costGroupId]
    };
  }

  async function confirmAndPrepareVendorBill() {
    // Check assignments based on mode
    const itemsToProcess = useSplitMode ? splitAssignments : assignedItems;
    
    if (!itemsToProcess?.length) {
      console.error('No assigned items available');
      return;
    }

    // Reset vendor-related state to ensure fresh data
    jtVendors = [];
    filteredVendors = [];
    vendorSearchQuery = "";
    selectedVendorId = null;
    autoMatchedVendor = false;
    autoMatchFailed = false;
    
    // Show vendor matching interface immediately
    showVendorMatching = true;
    
    // Start showing the searching message immediately
    isAutoMatchingVendor = true;
    
    // Force a fresh vendor search
    await searchVendors();
    
    // Try to automatically match vendor from the selected vendor bills
    await tryAutoMatchVendor();
    
    // Check if auto-match succeeded
    if (autoMatchedVendor) {
      // Auto-match succeeded - proceed automatically
      console.log('Vendor auto-matched, proceeding to create vendor bill automatically');
      
      // Brief delay to show success message
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Automatically create vendor bill
      await createVendorBill();
      
      // Hide the vendor matching section after bill creation
      showVendorMatching = false;
    } else if (pendingVendorCandidates.length > 0) {
      // Candidates found — show picker UI and wait for user confirmation
      console.log('Vendor candidates found, showing picker for user confirmation');
      setTimeout(() => {
        const vendorMatchingSection = document.getElementById('vendor-matching-section');
        if (vendorMatchingSection) {
          vendorMatchingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          window.scrollBy({ top: -20, behavior: 'smooth' });
        }
      }, 100);
    } else {
      // Auto-match failed - show manual UI
      console.log('Vendor auto-match failed, showing manual selection UI');
      
      // Scroll to vendor matching section
      setTimeout(() => {
        const vendorMatchingSection = document.getElementById('vendor-matching-section');
        if (vendorMatchingSection) {
          vendorMatchingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          // Add a small offset from the top for better visibility
          window.scrollBy({ top: -20, behavior: 'smooth' });
        }
      }, 100);
    }
  }

  // normalizeForComparison, stripAllWhitespace, isSimilarEnough, and
  // findVendorCandidates are imported from ../lib/vendorMatch.js

  async function searchVendors(query = "") {
    isLoadingVendors = true;
    vendorSearchQuery = query;
    
    try {
      // Add cache-busting timestamp to prevent browser caching
      const timestamp = new Date().getTime();
      const response = await fetch(`/api/jt/vendors?t=${timestamp}`);
      if (!response.ok) {
        throw new Error('Failed to fetch vendors');
      }
      
      jtVendors = await response.json();
      console.log('Fetched vendors:', jtVendors);
      
      // Filter vendors based on search query
      filterVendors();
    } catch (error) {
      console.error('Error fetching vendors:', error);
    } finally {
      isLoadingVendors = false;
    }
  }

  async function loadVendorAliases() {
    if (vendorAliasesLoaded) return;
    try {
      const response = await fetch('/api/users/vendor-aliases');
      if (response.ok) {
        const data = await response.json();
        vendorAliases = data.vendorAliases || {};
      }
    } catch (err) {
      console.warn('Could not load vendor aliases:', err.message);
    }
    vendorAliasesLoaded = true;
  }

  async function confirmVendorCandidate(vendor, sourceName) {
    selectedVendorId = vendor.id;
    vendorSearchQuery = vendor.name;
    autoMatchedVendor = true;
    pendingVendorCandidates = [];
    filterVendors();

    try {
      await fetch('/api/users/vendor-aliases', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sourceName, jtVendorId: vendor.id })
      });
      const key = normalizeForComparison(sourceName);
      vendorAliases[key] = vendor.id;
      console.log(`💾 Saved vendor alias: "${sourceName}" -> "${vendor.name}"`);
    } catch (err) {
      console.warn('Failed to save vendor alias:', err.message);
    }

    await createVendorBill();
    showVendorMatching = false;
  }

  function rejectVendorCandidates() {
    pendingVendorCandidates = [];
    autoMatchFailed = true;
  }

  async function tryAutoMatchVendor() {
    // Reset previous states
    autoMatchedVendor = false;
    autoMatchFailed = false;
    pendingVendorCandidates = [];

    if (jtVendors.length === 0) {
      autoMatchFailed = true;
      return;
    }

    await loadVendorAliases();

    // Add a small delay to show the loading state
    await new Promise(resolve => setTimeout(resolve, 500));

    let vendorNamesToMatch = [];

    // In Job mode, use the Xero bill's contact name
    if (isJobMode) {
      const xeroVendorName = currentBill?.contact?.name;
      if (xeroVendorName) {
        vendorNamesToMatch = [xeroVendorName];
        console.log('Job mode: Attempting to auto-match vendor from Xero bill contact:', xeroVendorName);
      }
    } else {
      // In PO mode, try to auto-match vendor based on vendor names from the matched vendor bills
      if (matchedVendorBills.length > 0) {
        vendorNamesToMatch = [...new Set(matchedVendorBills.map(bill => bill.vendorName).filter(name => name && name !== 'Unknown Vendor'))];
        console.log('PO mode: Attempting to auto-match vendor from PO vendor names:', vendorNamesToMatch);
      }
    }

    for (const vendorName of vendorNamesToMatch) {
      const { exactMatch, candidates } = findVendorCandidates(vendorName, jtVendors, vendorAliases);

      if (exactMatch) {
        console.log(`✅ Auto-matched: "${vendorName}" -> "${exactMatch.name}"`);
        selectedVendorId = exactMatch.id;
        autoMatchedVendor = true;
        vendorSearchQuery = exactMatch.name;
        filterVendors();
        break;
      }

      if (candidates.length > 0) {
        pendingVendorCandidates = candidates;
        pendingVendorSourceName = vendorName;
        console.log(`🔍 Found ${candidates.length} candidate(s) for "${vendorName}":`, candidates.map(v => v.name));
        break;
      }
    }

    if (!autoMatchedVendor && pendingVendorCandidates.length === 0) {
      console.log('❌ No automatic vendor match found');
      autoMatchFailed = true;
    }

    // Stop showing the searching message
    isAutoMatchingVendor = false;
  }

  function filterVendors() {
    if (!vendorSearchQuery || vendorSearchQuery.trim() === "") {
      filteredVendors = jtVendors;
    } else {
      const query = vendorSearchQuery.toLowerCase().trim();
      filteredVendors = jtVendors.filter(vendor => 
        vendor.name.toLowerCase().includes(query)
      );
    }
  }

  function handleVendorSearch(event) {
    vendorSearchQuery = event.target.value;
    // Reset auto-match status when user manually searches
    if (autoMatchedVendor || autoMatchFailed) {
      autoMatchedVendor = false;
      autoMatchFailed = false;
    }
    filterVendors();
  }

  function selectVendor(vendorId) {
    selectedVendorId = vendorId;
  }

  async function createNewVendor() {
    if (!currentBill?.contact?.name) {
      alert('No vendor information available from Xero');
      return;
    }
    if (!requiredCustomFieldsFilled(requiredVendorFields, requiredVendorFieldValues)) {
      requiredVendorError = requiredVendorError || 'Choose the required JobTread field, then create the vendor.';
      return;
    }

    isCreatingVendor = true;

    try {
      const vendorData = {
        name: currentBill.contact.name
      };

      if (currentBill.contact.emailAddress) {
        vendorData.email = currentBill.contact.emailAddress;
      }

      if (currentBill.contact.phoneNumber) {
        vendorData.phone = currentBill.contact.phoneNumber;
      }

      const customFieldValues = customFieldValuesFromSelections(requiredVendorFieldValues);
      if (Object.keys(customFieldValues).length) {
        vendorData.customFieldValues = customFieldValues;
      }

      const response = await fetch('/api/jt/createvendor', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(vendorData)
      });

      const result = await response.json().catch(() => ({}));
      if (isRequiredCustomFieldResult(result)) {
        requiredVendorFields = mergeRequiredCustomFields(requiredVendorFields, result.requiredCustomFields);
        requiredVendorError = result.error;
        return;
      }
      if (!response.ok) {
        throw new Error(result.error || result.message || `HTTP error! status: ${response.status}`);
      }

      const created = parseCreatedVendor(result, vendorData.name);
      if (!created) {
        throw new Error('Failed to get vendor ID from response');
      }

      if (!jtVendors.some(v => v.id === created.id)) {
        jtVendors = [...jtVendors, created];
      }
      selectedVendorId = created.id;
      requiredVendorFields = [];
      requiredVendorFieldValues = {};
      requiredVendorError = '';
      createVendorBill();
    } catch (error) {
      console.error('Error creating vendor:', error);
      alert('Failed to create vendor: ' + error.message);
    } finally {
      isCreatingVendor = false;
    }
  }

  function cancelVendorMatching() {
    showVendorMatching = false;
    selectedVendorId = null;
    autoMatchedVendor = false;
    isAutoMatchingVendor = false;
    autoMatchFailed = false;
    pendingVendorCandidates = [];
    pendingVendorSourceName = '';
    requiredVendorFields = [];
    requiredVendorFieldValues = {};
    requiredVendorError = '';
  }

  // Add this function to close the error modal
  function closeErrorModal() {
    showErrorModal = false;
    creationError = null;
  }

  async function createVendorBill() {
    const itemsToProcess = useSplitMode ? splitAssignments : assignedItems;

    if (!itemsToProcess?.length) {
      console.error('No assigned items available');
      return;
    }

    isCreatingVendorBill = true;
    vendorBillCreated = false;
    processingVendorBills = true;
    vendorBillsProcessed = 0;
    vendorBillsCreationResults = [];
    creationError = null;
    showErrorModal = false;
    vendorSearchQuery = '';
    filterVendors();

    try {
      if (!selectedVendorId) {
        throw new Error('No vendor selected. Please select a vendor before creating vendor bills.');
      }

      // Group assigned items by vendor bill ID
      const assignmentsByVendorBill = {};
      for (const item of itemsToProcess) {
        const vbId = item.vendorBillId;
        if (!vbId) continue;
        if (!assignmentsByVendorBill[vbId]) assignmentsByVendorBill[vbId] = [];
        assignmentsByVendorBill[vbId].push(item);
      }

      // Build slim billEntries for the server
      const billEntries = [];
      for (const [vendorBillId, vendorBillItems] of Object.entries(assignmentsByVendorBill)) {
        if (!vendorBillItems.length) continue;

        const isJobModeEntry = vendorBillId === 'job-mode';
        let jobId, vendorOrderId, vendorOrderNumber;

        if (isJobModeEntry) {
          const firstItem = vendorBillItems[0];
          if (!firstItem.costGroup) {
            vendorBillsCreationResults.push({ success: false, vendorBillId, error: 'Missing cost group assignment' });
            vendorBillsProcessed++;
            continue;
          }
          const costGroup = jobCostGroups.find(cg => cg.id === firstItem.costGroup.id);
          if (!costGroup?.jobId) {
            vendorBillsCreationResults.push({ success: false, vendorBillId, error: 'Invalid cost group data - missing jobId' });
            vendorBillsProcessed++;
            continue;
          }
          jobId = costGroup.jobId;
        } else {
          const bill = matchedVendorBills.find(b => b.id === vendorBillId);
          if (!bill) {
            vendorBillsCreationResults.push({ success: false, vendorBillId, error: 'Vendor bill not found' });
            vendorBillsProcessed++;
            continue;
          }
          jobId = bill.jobId;
          vendorOrderId = bill.vendorOrderId;
          vendorOrderNumber = bill.vendorOrderNumber;
        }

        if (!jobId) {
          vendorBillsCreationResults.push({ success: false, vendorBillId, error: 'Missing job ID' });
          vendorBillsProcessed++;
          continue;
        }

        const items = vendorBillItems
          .filter(item => {
            const u = item.invoiceLineItem?.unitAmount;
            return u != null && u !== 0 && !isNaN(u);
          })
          .map(item => ({
            invoiceLineItem: {
              description: item.invoiceLineItem.description,
              quantity: item.invoiceLineItem.quantity,
              unitAmount: item.invoiceLineItem.unitAmount,
              taxType: item.invoiceLineItem.taxType,
              taxAmount: item.invoiceLineItem.taxAmount,
              // Xero's own line total, so the server takes a tax-inclusive line back to
              // pre-tax from the line's own tax instead of a flat rate.
              lineAmount: item.invoiceLineItem.lineAmount,
              gstTreatment: item.invoiceLineItem.gstTreatment
            },
            originalItem: {
              id: item.originalItem?.id,
              name: item.originalItem?.name,
              costTypeId: item.originalItem?.costTypeId,
              jobCostItemId: item.originalItem?.jobCostItemId || item.originalItem?.id
            },
            costGroupId: item.costGroup?.id || null,
            isSplitAssignment: item.isSplitAssignment || false,
            hasGST: item.hasGST != null ? item.hasGST : null
          }));

        billEntries.push({
          mode: isJobModeEntry ? 'job' : 'po',
          jobId,
          vendorOrderId: vendorOrderId || null,
          vendorOrderNumber: vendorOrderNumber || null,
          items
        });
      }

      totalVendorBillsToProcess = billEntries.length;

      const apiPayload = {
        vendorId: selectedVendorId,
        xeroInvoiceID: currentBill.invoiceID,
        billInfo: {
          invoiceNumber: currentBill.invoiceNumber,
          invoiceID: currentBill.invoiceID,
          contactName: currentBill.contact?.name,
          date: currentBill.date,
          dueDate: currentBill.dueDate,
          lineAmountTypes: currentBill.lineAmountTypes,
          total: currentBill.total,
          // Xero's own lines, so the server can split Canadian PST (non-recoverable,
          // into cost) from GST (the bill's tax) by each line's Xero tax rate.
          taxLines: (currentBill.lineItems || []).map(li => ({ taxType: li.taxType, lineAmount: li.lineAmount, taxAmount: li.taxAmount }))
        },
        taxConfig: { rate: taxConfig.rate, name: taxConfig.name, nonRecoverableName: taxConfig.nonRecoverableName || null },
        billEntries
      };

      let response = await fetch('/api/jt/createvendorbill', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(apiPayload)
      });

      // JobTread already has a bill from this vendor with this invoice number:
      // ask before creating a second one.
      if (response.status === 409) {
        const dup = await response.clone().json().catch(() => ({}));
        if (dup.duplicateSupplierInvoice && window.confirm(`${dup.error}

OK = create it in JobTread anyway. Cancel = leave it.`)) {
          response = await fetch('/api/jt/createvendorbill', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...apiPayload, allowDuplicateSupplierInvoice: true })
          });
        }
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      // Handle single-entry response (backward compat shape)
      if (result.createDocument) {
        const doc = result.createDocument.createdDocument;
        if (doc) {
          vendorBillsCreationResults.push({
            success: true, vendorBillId: billEntries[0]?.mode === 'job' ? 'job-mode' : `${billEntries[0]?.jobId}-${billEntries[0]?.vendorOrderId}`,
            vendorOrderNumber: billEntries[0]?.vendorOrderNumber,
            documentId: doc.id, jobId: doc.job?.id,
            url: `https://app.jobtread.com/jobs/${doc.job?.id}/documents/${doc.id}`
          });
          if (!currentBill.createdDocument) {
            currentBill.createdDocument = { id: doc.id, jobId: doc.job?.id };
          }
        }
        vendorBillsProcessed++;
      } else if (result.results) {
        // Multi-entry response
        for (const r of result.results) {
          vendorBillsCreationResults.push(r);
          if (r.success && !currentBill.createdDocument) {
            currentBill.createdDocument = { id: r.documentId, jobId: r.jobId };
          }
          vendorBillsProcessed++;
        }
      }

      vendorBillCreated = vendorBillsCreationResults.some(r => r.success);

      // Show error modal if any failed
      const failed = vendorBillsCreationResults.find(r => !r.success);
      if (failed) {
        creationError = failed.error || 'An error occurred creating a vendor bill';
        showErrorModal = true;
      }

      console.log('Vendor bill creation completed:', {
        totalProcessed: vendorBillsProcessed,
        totalSuccessful: vendorBillsCreationResults.filter(r => r.success).length,
        results: vendorBillsCreationResults
      });

    } catch (error) {
      console.error('Error in createVendorBill:', error);
      creationError = error.message || 'An error occurred while preparing the vendor bills';
      showErrorModal = true;
    } finally {
      isCreatingVendorBill = false;
      processingVendorBills = false;
    }
  }

  // Helper function to check if GST is actually present on the line item
  function hasGST(lineItem) {
    // Check taxType first - if it's explicitly "NONE" or contains "EXEMPTOUTPUT", there's no GST
    if (lineItem.taxType) {
      const taxType = lineItem.taxType.toUpperCase();
      if (taxType === 'NONE' || taxType.includes('EXEMPTOUTPUT') || taxType.includes('NOGST')) {
        return false;
      }
    }

    // Check if taxAmount exists and is greater than 0
    const hasGSTAmount = lineItem.taxAmount != null && lineItem.taxAmount > 0;
    return hasGSTAmount;
  }

  async function fetchJobs() {
    try {
      let allJobs = [];
      let nextPage = null;
      
      do {
        const response = await fetch(`/api/jt/jobs${nextPage ? `?page=${nextPage}` : ''}`);
        const data = await response.json();
        
        if (data.jobs) {
          allJobs = [...allJobs, ...data.jobs];
        }
        
        nextPage = data.nextPage;
        console.log('Fetched jobs:', {
          currentCount: allJobs.length,
          hasNextPage: !!nextPage
        });
      } while (nextPage);
      
      customers = allJobs;
      console.log('Total jobs fetched:', customers.length);
      
      // Auto-select job from matching section in Job mode if no job is already selected
      if (isJobMode && currentBill && currentBill.lineItems) {
        const hasAnySelection = currentBill.lineItems.some(lineItem => 
          lineItemVendorBillSelections[lineItem.lineItemID]
        );
        
        if (!hasAnySelection) {
          // Find jobs that are in the matching section (matchedVendorBills with type 'job')
          const jobsInMatching = matchedVendorBills.filter(bill => bill.type === 'job');
          
          if (jobsInMatching.length > 0) {
            // Use the first job from the matching section (only auto-select if there's an actual match)
            const selectedJob = jobsInMatching[0];
            currentBill.lineItems.forEach(lineItem => {
              lineItemVendorBillSelections[lineItem.lineItemID] = selectedJob.jobId;
            });
            // Trigger reactivity
            lineItemVendorBillSelections = { ...lineItemVendorBillSelections };
            
            // Populate the Job search field with the matched job
            customerSearchQuery = selectedJob.jobName || selectedJob.name || '';
            selectedCustomer = selectedJob.jobId;
            
            // Fetch cost groups for the selected job
            await fetchJobCostGroups(selectedJob.jobId);
          }
          // If no match in matching section, don't auto-select - let user manually choose
        }
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  }

  /**
   * Attempt to auto-assign line items based on their Xero account codes
   * Uses reverse mapping: Xero account code -> JT cost code -> matching cost item in job
   */
  function tryAutoAssignFromAccountCodes() {
    if (!lineItemsData?.costGroups || !currentBill?.lineItems) {
      console.log('Cannot auto-assign: missing cost groups or line items');
      return;
    }

    if (Object.keys(reverseCostCodeMappings).length === 0) {
      console.log('Cannot auto-assign: no cost code mappings loaded');
      return;
    }

    console.log('🔍 Attempting auto-assignment from Xero account codes...');
    let assignedCount = 0;

    for (const lineItem of currentBill.lineItems) {
      // Skip if already assigned
      if (assignedItems.find(a => a.invoiceLineItem.lineItemID === lineItem.lineItemID)) {
        continue;
      }

      const xeroAccountCode = lineItem.accountCode;
      if (!xeroAccountCode) {
        console.log(`Line "${lineItem.description}": No accountCode, skipping`);
        continue;
      }

      // Reverse lookup: Xero code -> JT cost code(s)
      const jtCostCodes = reverseCostCodeMappings[xeroAccountCode];
      if (!jtCostCodes || jtCostCodes.length === 0) {
        console.log(`Line "${lineItem.description}": No mapping for Xero code ${xeroAccountCode}`);
        continue;
      }

      // Find cost item in job cost groups matching any of the JT codes
      // Match by: (a) item's costCode.number, or (b) item's parentCostCode.number (for child items)
      const codesStr = new Set(jtCostCodes.map(String));
      const matchesCode = (num) => num != null && codesStr.has(String(num));
      let foundMatch = false;
      for (const costGroup of lineItemsData.costGroups) {
        const groupIndex = lineItemsData.costGroups.indexOf(costGroup);
        for (const item of costGroup.lineItems || []) {
          if (item.costCode && (matchesCode(item.costCode.number) || matchesCode(item.costCode.parentCostCode?.number))) {
            console.log(`✅ Auto-assigning "${lineItem.description}" to "${item.name}" (${item.costCode.number})`);
            selectAssignmentItem(lineItem, groupIndex, item.id, true); // true = isAutoAssign
            assignedCount++;
            foundMatch = true;
            break;
          }
        }
        if (foundMatch) break;
      }

      if (!foundMatch) {
        console.log(`Line "${lineItem.description}": No matching cost item for JT codes [${jtCostCodes.join(',')}]`);
      }
    }

    console.log(`🔍 Auto-assignment complete: ${assignedCount} items assigned`);
  }

  /**
   * Run PO mode 2-step auto-match from line item tracking.
   * Used by loadCurrentBill and when user switches toggle to PO mode.
   */
  async function runPOModeAutoMatch() {
    if (!currentBill?.lineItems?.length || !customers.length) return;
    const jobValue = getJobValueFromLineItems(currentBill.lineItems, billTrackingSettings, 'jtBillNameField');
    if (!jobValue) return;
    const matchingJob = customers.find(job => jobMatchesTrackingValue(job, jobValue));
    if (!matchingJob) return;
    console.log(`Auto-matched job from tracking '${jobValue}':`, matchingJob.name);
    isAutoMatched = true;
    selectedCustomer = matchingJob.id;
    customerSearchQuery = matchingJob.name;
    const jobEntry = { id: `job-${matchingJob.id}`, type: 'job', jobId: matchingJob.id, jobName: matchingJob.name };
    matchedVendorBills = [jobEntry];
    const allPOValues = getAllPOValuesFromLineItems(currentBill.lineItems);
    matchedPOCount.total = allPOValues.length || 1;
    matchedPOCount.found = 0;
    multipleMatchedPOs = allPOValues.length > 1;
    setTimeout(async () => {
      isLoadingVendorOrders = true;
      try {
        const response = await fetch(`/api/jt/documents?jtjobid=${selectedCustomer}`);
        const data = await response.json();
        vendorOrders = Array.isArray(data) ? data : (data.orders || []);
        const poValue = getPOValueFromLineItems(currentBill.lineItems);
        let matchingOrder = null;
        if (poValue && vendorOrders.length > 0) {
          matchingOrder = vendorOrders.find(o =>
            normalizePO(o.number) === normalizePO(poValue) ||
            (o.job?.number && `${o.job.number}-${o.number}` === poValue) ||
            (o.fullName && o.fullName.endsWith(`-${poValue}`))
          );
          if (!matchingOrder && poValue.includes('-')) {
            const numPart = poValue.split('-').pop();
            matchingOrder = vendorOrders.find(o => normalizePO(o.number) === normalizePO(numPart));
          }
        }
        if (matchingOrder && matchingOrder.number != null) {
          selectedVendorOrder = matchingOrder.id;
          matchedPOCount.found = 1;
          const poBill = {
            id: `${selectedCustomer}-${matchingOrder.id}`,
            type: 'po',
            jobId: selectedCustomer,
            jobName: matchingJob.name,
            vendorOrderId: matchingOrder.id,
            vendorOrderNumber: matchingOrder.number,
            vendorName: matchingOrder.account?.name || 'Unknown',
            poNumber: poValue,
            lineItems: null
          };
          matchedVendorBills = [jobEntry, poBill];
          await selectVendorOrder(selectedVendorOrder, false);
          matchedVendorBills = matchedVendorBills.map(b => b.vendorOrderId === selectedVendorOrder ? { ...b, lineItems: lineItemsData } : b);
          await autoAssignLineItems();
        } else {
          // Job was found via tracking but no matching PO — switch to Job Mode.
          // The reactive ($: isJobMode !== previousMode) will auto-select the job entry
          // already in matchedVendorBills and load its cost groups.
          console.log(`Job "${matchingJob.name}" matched but no PO found — switching to Job Mode`);
          isJobMode = true;
        }
      } catch (err) {
        console.error('Error fetching vendor orders:', err);
      } finally {
        isLoadingVendorOrders = false;
      }
    }, 100);
  }

  /**
   * Attempt to auto-match job based on 'Job Name' tracking category from Xero line items
   * Uses contains matching: searches for jobs whose name contains the tracking value
   * Only uses the FIRST Job Name value found across all line items
   */
  async function tryAutoMatchJobFromTrackingCategory() {
    if (!isJobMode || !currentBill?.lineItems || customers.length === 0) {
      console.log('Cannot auto-match job: not in Job mode, no line items, or no jobs loaded');
      return;
    }

    // Check if we already have jobs in matching section
    const existingJobs = matchedVendorBills.filter(bill => bill.type === 'job');
    if (existingJobs.length > 0) {
      console.log('Jobs already in matching section, skipping auto-match from tracking');
      return;
    }

    const jobNameValue = getJobValueFromLineItems(currentBill.lineItems, billTrackingSettings, 'jtBillNameField');
    if (!jobNameValue) {
      console.log('No Job Name or custom tracking category found in line items');
      return;
    }
    console.log('Found job value from tracking:', jobNameValue);

    const matchingJob = customers.find(job => jobMatchesTrackingValue(job, jobNameValue));

    if (matchingJob) {
      console.log(`Auto-matched job from tracking category '${jobNameValue}':`, matchingJob.name);
      
      // Add the job to matching section (same logic as addVendorBill in Job mode)
      const newJobEntry = {
        id: `job-${matchingJob.id}`,
        type: 'job',
        jobId: matchingJob.id,
        jobName: matchingJob.name
      };
      
      matchedVendorBills = [...matchedVendorBills, newJobEntry];
      
      // Assign all line items to this job
      const updatedSelections = {};
      currentBill.lineItems.forEach(lineItem => {
        updatedSelections[lineItem.lineItemID] = matchingJob.id;
      });
      lineItemVendorBillSelections = updatedSelections;
      
      // Populate the Job search field
      customerSearchQuery = matchingJob.name;
      selectedCustomer = matchingJob.id;
      
      // Fetch cost groups for this job
      await fetchJobCostGroups(matchingJob.id);
      
      console.log('Auto-added job to matching section from tracking category');
    } else {
      console.log(`No matching job found for tracking category value '${jobNameValue}'`);
    }
  }

  async function fetchJobCostGroups(jobId, forceRefresh = false) {
    isLoadingCostGroups = true;
    try {
      console.log(`Fetching cost groups for job: ${jobId}${forceRefresh ? ' (force refresh)' : ''}`);
      // Use the endpoint that gets cost groups with their existing cost items
      const url = `/api/jt/jobs/${jobId}/cost-groups-with-items${forceRefresh ? '?refresh=true' : ''}`;
      const response = await fetch(url);
      
      if (!response.ok) {
        let errorMessage = 'Failed to fetch cost groups with items';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
          
          // If server indicated we need to login, show a more helpful message
          if (errorData.needsLogin) {
            errorMessage = 'Session expired. Please refresh the page and log in again.';
          }
        } catch (e) {
          // If we can't parse the error response as JSON, use default message
          console.error('Could not parse error response:', e);
        }
        throw new Error(errorMessage);
      }
      
      // Parse response and validate it's actually JSON
      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        console.error('Failed to parse response as JSON:', jsonError);
        throw new Error('Server returned invalid response. This may indicate an authentication or API error. Please try refreshing the page.');
      }
      
      // Track cache status
      costGroupsCached = data.cached || false;
      costGroupsCachedAt = data.cachedAt || null;
      
      console.log(`✅ Cost groups loaded (cached: ${costGroupsCached})`);
      
      // Transform the data to match the lineItemsData structure used by PO mode
      // This ensures the UI can display cost groups with their cost items
      const transformedData = {
        costGroups: data.costGroups
          .filter(group => !/- Bill .+ - (Job Mode|PO #)/i.test(group.name || ''))
          .map(group => ({
            id: group.id,
            name: group.name,
            description: group.description,
            position: group.position,
            parentCostGroup: group.parentCostGroup || null,
            isSelected: group.isSelected || false,
            jobId: group.jobId || null,
            lineItems: group.costItems || []
          }))
      };
      
      // Store both the original cost groups (for dropdown) and the detailed structure (for assignments)
      jobCostGroups = transformedData.costGroups;
      
      // Force reactivity by creating a new object reference
      lineItemsData = { ...transformedData }; // This is what PO mode uses for assignments
      
      console.log(`Fetched ${jobCostGroups.length} cost groups for job ${jobId}`);
      console.log('Job cost groups with items:', transformedData);
      console.log('🔍 LineItemsData after assignment:', lineItemsData);
      
      // Try to auto-assign line items based on Xero account codes (Job mode only)
      if (isJobMode && currentBill?.lineItems) {
        tryAutoAssignFromAccountCodes();
      }

      // Auto-expand first assignment dropdown only if items remain unassigned
      autoExpandFirstAssignmentDropdown();
      
      return lineItemsData;
    } catch (error) {
      console.error('❌ Error fetching job cost groups with items:', error);
      
      // Show user-friendly error message
      let userMessage = 'Failed to load cost groups. ';
      if (error.message.includes('Session expired') || error.message.includes('not authenticated')) {
        userMessage += 'Your session has expired. Please refresh the page and log in again.';
      } else if (error.message.includes('invalid response')) {
        userMessage += 'The server returned an unexpected response. Please try again or refresh the page.';
      } else {
        userMessage += error.message;
      }
      
      // Log for debugging
      console.error(`Failed to fetch cost groups with items: ${error.message}`);
      
      // You could show a toast/alert here if you have a notification system
      alert(userMessage);
      
      return null;
    } finally {
      isLoadingCostGroups = false;
    }
  }

  async function refreshCostGroups() {
    // Find all jobs in the matching section
    const jobsInMatching = matchedVendorBills.filter(bill => bill.type === 'job');
    
    if (jobsInMatching.length === 0) {
      console.warn('No jobs in matching section to refresh');
      return;
    }
    
    console.log(`🔄 Refreshing cost groups for ${jobsInMatching.length} job(s)`);
    
    // Refresh data for each job
    for (const job of jobsInMatching) {
      console.log(`🔄 Refreshing job: ${job.jobName || job.name} (${job.jobId})`);
      await fetchJobCostGroups(job.jobId, true);
    }
    
    console.log('✅ Finished refreshing all job cost groups');
  }

  // Function to toggle the inline add form (only used when matches exist)
  function toggleInlineAddForm() {
    showInlineAddForm = !showInlineAddForm;
    
    // Clear form values when opening the form
    if (showInlineAddForm) {
      customerSearchQuery = '';
      vendorOrderSearchQuery = '';
      selectedCustomer = null;
      selectedVendorOrder = null;
      showCustomerDropdown = false;
      showVendorOrderDropdown = false;
    }
  }
  
  // New function to add a vendor bill to the matched list
  async function addVendorBill() {
    console.log('🔥 addVendorBill called - isJobMode:', isJobMode, 'selectedCustomer:', selectedCustomer);
    
    if (!selectedCustomer) {
      console.error('Cannot add vendor bill: Customer/Job not selected');
      return;
    }
    
    // In PO mode, require vendor order selection
    if (!isJobMode && !selectedVendorOrder) {
      console.error('Cannot add vendor bill: Vendor Order not selected in PO mode');
      return;
    }
    
    const selectedJob = customers.find(job => job.id === selectedCustomer);
    console.log('🔥 Selected job found:', selectedJob);
    
    if (isJobMode) {
      // Job mode: Add job to matching list and fetch cost groups
      const existingJob = matchedVendorBills.find(bill => 
        bill.jobId === selectedCustomer && bill.type === 'job'
      );
      
      if (existingJob) {
        console.log('This job is already in the list');
        return;
      }
      
      // Create a job entry
      const newJobEntry = {
        id: `job-${selectedCustomer}`,
        type: 'job',
        jobId: selectedCustomer,
        jobName: selectedJob?.name || 'Unknown Job'
      };
      
      console.log('🔥 Creating new job entry:', newJobEntry);
      const isFirstJob = matchedVendorBills.filter(b => b.type === 'job').length === 0;
      matchedVendorBills = [...matchedVendorBills, newJobEntry];
      console.log('🔥 Updated matchedVendorBills:', matchedVendorBills);
      
      // IMPORTANT: Assign line items to the job BEFORE fetching cost groups
      // This ensures lineItemVendorBillSelections is set when the dropdown tries to render
      if (currentBill && currentBill.lineItems) {
        const updatedSelections = { ...lineItemVendorBillSelections };
        currentBill.lineItems.forEach(lineItem => {
          const currentSelection = updatedSelections[lineItem.lineItemID];
          // If it's the first job, assign to ALL line items
          // Otherwise, only assign to unassigned ones
          if (isFirstJob || currentSelection === undefined || currentSelection === null || currentSelection === 'select' || currentSelection === '') {
            updatedSelections[lineItem.lineItemID] = selectedCustomer;
          }
        });
        // Trigger reactivity
        lineItemVendorBillSelections = updatedSelections;
        console.log(`🔥 ${isFirstJob ? 'First Job - Auto-assigned to ALL' : 'Auto-assigned to unassigned'} line items:`, lineItemVendorBillSelections);
      }
      
      console.log('🔥 Fetching cost groups for job:', selectedCustomer);
      // Fetch cost groups for this job AFTER line item assignments are set
      await fetchJobCostGroups(selectedCustomer);
    } else {
      // PO mode: Fetch line items for the vendor order first
      const existingBill = matchedVendorBills.find(bill => 
        bill.jobId === selectedCustomer && bill.vendorOrderId === selectedVendorOrder
      );
      
      if (existingBill) {
        console.log('This vendor bill is already in the list');
        return;
      }
      
      // Find the selected vendor order details
      const selectedOrder = vendorOrders.find(order => order.id === selectedVendorOrder);
      if (!selectedOrder) {
        console.error('Selected vendor order not found in list');
        return;
      }
      
      console.log('🔥 Fetching line items for vendor order:', selectedVendorOrder);
      
      // Ensure we have the latest line items data
      try {
        const response = await fetch(`/api/jt/lineitems?jtjobid=${selectedVendorOrder}`);
        if (!response.ok) {
          throw new Error('Failed to fetch line items');
        }
        const freshLineItemsData = await response.json();
        console.log('🔥 Fetched fresh line items data:', {
          hasCostGroups: !!freshLineItemsData?.costGroups,
          costGroupCount: freshLineItemsData?.costGroups?.length || 0,
          totalItems: freshLineItemsData?.costGroups?.reduce((sum, g) => sum + (g.lineItems?.length || 0), 0) || 0
        });
        
        // Create a new vendor bill object with the fetched line items
        const newBill = {
          id: `${selectedCustomer}-${selectedVendorOrder}`,
          type: 'po',
          jobId: selectedCustomer,
          jobName: selectedJob?.name || 'Unknown Job',
          vendorOrderId: selectedVendorOrder,
          vendorOrderNumber: selectedOrder.number || 'Unknown PO',
          vendorName: selectedOrder.account?.name || 'Unknown Vendor',
          lineItems: structuredClone(freshLineItemsData)
        };
        
        console.log('🔥 Created new vendor bill with lineItems:', {
          billId: newBill.id,
          hasCostGroups: !!newBill.lineItems?.costGroups,
          costGroupCount: newBill.lineItems?.costGroups?.length || 0,
          totalItems: newBill.lineItems?.costGroups?.reduce((sum, g) => sum + (g.lineItems?.length || 0), 0) || 0
        });
      
        const isFirstPO = matchedVendorBills.filter(b => b.type === 'po').length === 0;
        matchedVendorBills = [...matchedVendorBills, newBill];
        console.log('Added new vendor bill to array:', newBill);
        console.log('🔥 All matched vendor bills after adding:', matchedVendorBills.map(b => ({
          id: b.id,
          type: b.type,
          hasCostGroups: !!b.lineItems?.costGroups,
          costGroupCount: b.lineItems?.costGroups?.length || 0
        })));

        // If this is the first PO, auto-assign to ALL line items
        // Otherwise, only assign to unassigned line items
        if (currentBill && Array.isArray(currentBill.lineItems)) {
          const updatedSelections = { ...lineItemVendorBillSelections };
          for (const lineItem of currentBill.lineItems) {
            const currentSelection = updatedSelections[lineItem.lineItemID];
            // If it's the first PO, assign to ALL line items
            // Otherwise, only assign to unassigned ones
            if (isFirstPO || currentSelection === undefined || currentSelection === null || currentSelection === 'select' || currentSelection === '') {
              updatedSelections[lineItem.lineItemID] = newBill.id;
            }
          }
          lineItemVendorBillSelections = updatedSelections; // trigger reactivity
          console.log(`${isFirstPO ? 'First PO - Auto-assigned to ALL' : 'Auto-assigned to unassigned'} line items:`, lineItemVendorBillSelections);
        }
      } catch (error) {
        console.error('🔥 Error fetching line items for vendor order:', error);
        alert('Failed to fetch line items for this purchase order. Please try again.');
        return;
      }
    }
    
    // Close the inline form and reset form values
    showInlineAddForm = false;
    customerSearchQuery = '';
    vendorOrderSearchQuery = '';
    selectedCustomer = null;
    selectedVendorOrder = null;
  }
  
    // New function to select a vendor bill or job for a line item
  async function selectVendorBillForLineItem(lineItemId, selectedId) {
    lineItemVendorBillSelections = {
      ...lineItemVendorBillSelections,
      [lineItemId]: selectedId
    };
    console.log('Updated line item selections:', lineItemVendorBillSelections);

    // If something was selected (not just unselected)
    if (selectedId) {
      if (isJobMode) {
        // In Job mode, selectedId is a jobId - fetch cost groups for this job
        console.log(`Job mode: fetching cost groups for job ${selectedId}`);
        selectedCustomer = selectedId; // Set selectedCustomer so the refresh button knows which job
        await fetchJobCostGroups(selectedId);
      } else {
        // In PO mode, selectedId is a vendorBillId - find the selected bill
        const selectedBill = matchedVendorBills.find(bill => bill.id === selectedId);
        
        // If the bill exists but doesn't have line items loaded yet, fetch them
        if (selectedBill && !selectedBill.lineItems) {
          console.log(`Fetching line items for vendor bill with ID ${selectedBill.vendorOrderId}`);

          try {
            const response = await fetch(`/api/jt/lineitems?jtjobid=${selectedBill.vendorOrderId}`);
            if (!response.ok) {
              throw new Error('Failed to fetch line items for the selected vendor bill');
            }
            
            const lineItemsData = await response.json();
            
            // Update the line items in the matched vendor bills array
            matchedVendorBills = matchedVendorBills.map(bill => {
              if (bill.id === selectedId) {
                return {
                  ...bill,
                  lineItems: lineItemsData
                };
              }
              return bill;
            });
            
            console.log(`Successfully loaded line items for vendor bill ${selectedId}`);
          } catch (error) {
            console.error('Error fetching line items for vendor bill:', error);
            alert('Failed to load line items for the selected vendor bill: ' + error.message);
          }
        }
      }
    }
  }
  
  
  // New function to remove a vendor bill from the matched list
  function removeVendorBill(vendorBillId) {
    matchedVendorBills = matchedVendorBills.filter(bill => bill.id !== vendorBillId);
    
    // Update line item selections to remove references to this bill
    for (const lineItemId in lineItemVendorBillSelections) {
      if (lineItemVendorBillSelections[lineItemId] === vendorBillId) {
        const newSelections = { ...lineItemVendorBillSelections };
        delete newSelections[lineItemId];
        lineItemVendorBillSelections = newSelections;
      }
    }
  }

  // New function to auto-assign line items to vendor bills
  // Uses the first PO found in any line item and applies it to ALL line items
  async function autoAssignLineItems() {
    console.log('Auto-assigning line items to vendor bills - using first PO match for all lines');
    
    if (!currentBill.lineItems || currentBill.lineItems.length === 0) return;

    // Get the first PO value from any line item (bill-level, not per-line)
    const poValue = getPOValueFromLineItems(currentBill.lineItems);
    
    // Find the matching vendor bill (one with vendorOrderNumber - the actual PO)
    let matchingBill = null;
    if (poValue && matchedVendorBills.length > 0) {
      matchingBill = matchedVendorBills.find(bill => 
        normalizePO(bill.poNumber) === normalizePO(poValue) || 
        normalizePO(bill.vendorOrderNumber) === normalizePO(poValue)
      );
      if (!matchingBill && poValue.includes('-')) {
        const numPart = poValue.split('-').pop();
        matchingBill = matchedVendorBills.find(bill => normalizePO(bill.vendorOrderNumber) === normalizePO(numPart));
      }
      if (!matchingBill) {
        // Fallback: use first bill that has an actual PO number (excludes job-only entries)
        matchingBill = matchedVendorBills.find(bill => bill.vendorOrderNumber != null);
      }
    }
    if (!matchingBill && matchedVendorBills.length > 0) {
      // Only fall back to a non-job entry — never assign to a job-only entry as a "PO"
      matchingBill = matchedVendorBills.find(bill => bill.vendorOrderNumber != null);
    }

    // If we only have a job-only entry (type: 'job', no vendorOrderNumber), bail out gracefully.
    // The user is effectively in job mode for this bill — no PO to assign to.
    if (!matchingBill) {
      console.log('No PO-type vendor bill found — skipping auto-assignment (job-only match, no PO)');
      return;
    }

    console.log(`Using vendor bill "${matchingBill.jobName} - PO #${matchingBill.vendorOrderNumber}" for all ${currentBill.lineItems.length} line items`);
    
    // Apply the same vendor bill to ALL line items
    const newSelections = { ...lineItemVendorBillSelections };
    for (const lineItem of currentBill.lineItems) {
      newSelections[lineItem.lineItemID] = matchingBill.id;
    }
    lineItemVendorBillSelections = newSelections;
    
    console.log('Completed auto-assignment of line items to vendor bills:', lineItemVendorBillSelections);
  }

  function handleCustomerSearch(event) {
    customerSearchQuery = event.target.value;
    filterCustomers();
  }
  
  function filterCustomers() {
    if (!customerSearchQuery || customerSearchQuery.trim() === '') {
      filteredCustomers = customers;
    } else {
      const query = customerSearchQuery.toLowerCase().trim();
      filteredCustomers = customers.filter(customer => 
        customer.name.toLowerCase().includes(query)
      );
    }
  }
  
  async function selectFilteredCustomer(customerId) {
    selectedCustomer = customerId;
    const selectedCust = customers.find(c => c.id === customerId);
    if (selectedCust) {
      customerSearchQuery = selectedCust.name;
    }
    showCustomerDropdown = false;
    
    // In PO mode, trigger the original select handler logic
    if (!isJobMode) {
      // Wait for vendor orders to load
      await handleCustomerSelect({ target: { value: customerId } });
      
      // After vendor orders are loaded, auto-expand dropdown and focus the field
      setTimeout(() => {
        if (vendorOrderInputElement) {
          showVendorOrderDropdown = true;
          filteredVendorOrders = vendorOrders;
          vendorOrderInputElement.focus();
        }
      }, 100);
    } else {
      // In Job Mode, automatically add the job to matching
      await addVendorBill();
    }
  }

  function handleVendorOrderSearch(event) {
    vendorOrderSearchQuery = event.target.value;
    filterVendorOrders();
  }
  
  function filterVendorOrders() {
    if (!vendorOrderSearchQuery || vendorOrderSearchQuery.trim() === '') {
      filteredVendorOrders = vendorOrders;
    } else {
      const query = vendorOrderSearchQuery.toLowerCase().trim();
      filteredVendorOrders = vendorOrders.filter(order => 
        order.number.toString().toLowerCase().includes(query) ||
        (order.subject && order.subject.toLowerCase().includes(query))
      );
    }
  }
  
  async function selectFilteredVendorOrder(orderId) {
    await selectVendorOrder(orderId, true);
    const selectedOrder = vendorOrders.find(o => o.id === orderId);
    if (selectedOrder) {
      vendorOrderSearchQuery = `PO #${selectedOrder.number}`;
    }
    showVendorOrderDropdown = false;
    
    // In PO mode, automatically add the PO to matching
    if (!isJobMode && selectedCustomer) {
      await addVendorBill();
    }
  }

  // Add this function to show all vendor orders when clicking the field
  function showVendorOrdersList() {
    showVendorOrderDropdown = true;
    // Initialize with all vendor orders
    filteredVendorOrders = vendorOrders;
  }

  // Functions for per-line-item cost group management
  function handleLineItemCostGroupSearch(lineItemId, event) {
    const query = event.target.value;
    lineItemCostGroupSearchQueries[lineItemId] = query;
    filterLineItemCostGroups(lineItemId);
  }
  
  function filterLineItemCostGroups(lineItemId) {
    const query = lineItemCostGroupSearchQueries[lineItemId] || '';
    if (!query || query.trim() === '') {
      lineItemFilteredCostGroups[lineItemId] = jobCostGroups;
    } else {
      const queryLower = query.toLowerCase().trim();
      lineItemFilteredCostGroups[lineItemId] = jobCostGroups.filter(costGroup => 
        cleanCostGroupName(costGroup.name).toLowerCase().includes(queryLower) ||
        (costGroup.description && costGroup.description.toLowerCase().includes(queryLower))
      );
    }
    // Trigger reactivity
    lineItemFilteredCostGroups = { ...lineItemFilteredCostGroups };
  }
  


  function showLineItemCostGroupsList(lineItemId) {
    lineItemCostGroupDropdowns[lineItemId] = true;
    // Initialize with all cost groups
    lineItemFilteredCostGroups[lineItemId] = jobCostGroups;
    
    // Trigger reactivity
    lineItemCostGroupDropdowns = { ...lineItemCostGroupDropdowns };
    lineItemFilteredCostGroups = { ...lineItemFilteredCostGroups };
  }

  function handleLineItemCostGroupFocusOut(lineItemId) {
    setTimeout(() => {
      lineItemCostGroupDropdowns[lineItemId] = false;
      lineItemCostGroupDropdowns = { ...lineItemCostGroupDropdowns };
    }, 200);
  }


</script>

<Layout activeRoute="import-bills-from-xero">
  <div class="process-selected-invoices text-sm flex flex-col h-full">
    <!-- Scrollable Content Area -->
    <div class="flex-1 overflow-y-auto pb-24">
      {#if error}
        <p class="error">{error}</p>
      {:else if !currentBill}
        <p>Loading bill data...</p>
      {:else}
      {@const billHasGST = currentBill.lineItems && currentBill.lineItems.some(lineItem => hasGST(lineItem))}
      {@const totalAmount = currentBill.total || 0}
      {@const subtotal = Number.isFinite(Number(currentBill.subTotal)) ? Number(currentBill.subTotal) : (billHasGST && taxConfig.rate > 0 ? totalAmount / (1 + taxConfig.rate) : totalAmount)}
      {@const gstAmount = Number.isFinite(Number(currentBill.totalTax)) ? Number(currentBill.totalTax) : (billHasGST ? totalAmount - subtotal : 0)}
      {@const displayTotal = totalAmount}

      <div class="bill-layout mb-4">
        <!-- Bill details card -->
        <div class="bill-details bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <!-- Wide layout: stacked card -->
          <div class="bill-details-stacked">
            <div class="bg-gradient-to-r from-slate-700 to-slate-800 px-4 py-4">
              <h1 class="text-sm font-semibold text-slate-300 uppercase tracking-wider">Processing Bill</h1>
              <h2 class="text-xl font-bold text-white mt-1">{currentBill.invoiceNumber || 'N/A'}</h2>
            </div>
            <div class="px-4 py-3 space-y-2.5">
              <div class="flex items-center gap-2">
                <svg class="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/></svg>
                <span class="text-base text-gray-800 font-medium truncate">{currentBill.contact?.name || 'N/A'}</span>
              </div>
              <div class="flex gap-4 text-xs text-gray-500">
                <div class="flex items-center gap-1.5">
                  <svg class="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                  {new Date(currentBill.date).toLocaleDateString()}
                </div>
                <div class="flex items-center gap-1.5">
                  <svg class="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                  Due {new Date(currentBill.dueDate).toLocaleDateString()}
                </div>
              </div>
              <div class="border-t border-gray-100 pt-2.5">
                <div class="flex items-baseline justify-between">
                  <span class="text-xs text-gray-400 uppercase tracking-wide">Total</span>
                  <span class="text-lg font-bold text-gray-900">
                    {new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(displayTotal)}
                  </span>
                </div>
                {#if billHasGST}
                  <p class="text-xs text-gray-400 text-right mt-0.5">
                    {new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(subtotal)} + {taxConfig.name} {new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(gstAmount - caBillPst)}{#if caBillPst} + PST {new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(caBillPst)}{/if}
                  </p>
                {/if}
              </div>
              <div class="flex items-center gap-2 pt-0.5">
                <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium {currentBill.status?.toUpperCase() === 'AUTHORISED' ? 'bg-green-50 text-green-700 ring-1 ring-green-600/20' : 'bg-gray-100 text-gray-600 ring-1 ring-gray-500/10'}">
                  {currentBill.status}
                </span>
                <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-50 text-slate-600 ring-1 ring-slate-500/10">
                  {currentBill.type}
                </span>
              </div>
            </div>
          </div>
          <!-- Narrow layout: 3-row compact card -->
          <div class="bill-details-horizontal">
            <!-- Row 1: Processing Bill heading + bill details -->
            <div class="bg-gradient-to-r from-slate-700 to-slate-800 px-4 py-2.5">
              <h1 class="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-1.5">Processing Bill</h1>
              <div class="flex items-center gap-3">
                <span class="text-sm font-semibold text-white">{currentBill.invoiceNumber || 'N/A'}</span>
                <div class="h-4 w-px bg-slate-600"></div>
                <div class="flex items-center gap-1.5 min-w-0">
                  <svg class="w-3.5 h-3.5 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/></svg>
                  <span class="text-sm text-white font-medium truncate">{currentBill.contact?.name || 'N/A'}</span>
                </div>
                <div class="h-4 w-px bg-slate-600"></div>
                <div class="flex items-center gap-4 text-xs text-slate-300">
                  <div class="flex items-center gap-1">
                    <svg class="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                    {new Date(currentBill.date).toLocaleDateString()}
                  </div>
                  <div class="flex items-center gap-1">
                    <svg class="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                    Due {new Date(currentBill.dueDate).toLocaleDateString()}
                  </div>
                </div>
              </div>
            </div>
            <!-- Row 2: Status badges -->
            <div class="bg-white px-4 py-1.5 flex items-center gap-2 border-t border-gray-200">
              <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium {currentBill.status?.toUpperCase() === 'AUTHORISED' ? 'bg-green-50 text-green-700 ring-1 ring-green-600/20' : 'bg-gray-100 text-gray-600 ring-1 ring-gray-500/10'}">
                {currentBill.status}
              </span>
              <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-50 text-slate-600 ring-1 ring-slate-500/10">
                {currentBill.type}
              </span>
            </div>
            <!-- Row 3: Total in white -->
            <div class="bg-white px-4 py-2 flex items-baseline gap-2 border-t border-gray-200">
              <span class="text-xs text-gray-400 uppercase tracking-wide">Total</span>
              <span class="text-lg font-bold text-gray-900">
                {new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(displayTotal)}
              </span>
              {#if billHasGST}
                <span class="text-xs text-gray-400">
                  ({new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(subtotal)} + {taxConfig.name} {new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(gstAmount - caBillPst)}{#if caBillPst} + PST {new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(caBillPst)}{/if})
                </span>
              {/if}
            </div>
          </div>
        </div>

        <!-- Vendor Bills being Matched section -->
        <div class="matched-vendor-bills bg-white rounded-lg border border-gray-200 p-3">
          <div class="mb-2">
            <h2 class="text-lg font-semibold">Select a Job to Match this invoice:</h2>
          </div>
          
          {#if isAutoMatched}
            <div class="mb-3 p-2 bg-green-100 border border-green-300 rounded-md flex items-center">
              <span class="text-green-700 mr-2">✓</span>
              {#if multipleMatchedPOs}
                <span>Multiple unique Jobtread PO numbers found.</span>
              {:else if matchedPOCount.total > 0}
                <span>{matchedPOCount.found}/{matchedPOCount.total} Jobtread PO numbers matched</span>
              {:else}
              <span>Pre-matched to PO based on Xero Reference</span>
              {/if}
            </div>
          {/if}
          
          <!-- Show matched items if any exist -->
          {#if matchedVendorBills.length > 0}
            <div class="space-y-1 max-h-60 overflow-y-auto mb-3">
              {#each matchedVendorBills as bill}
                <div class="bg-gray-50 border border-gray-200 rounded p-2">
                  <div class="flex justify-between">
                    <div>
                      <p class="font-medium">{bill.jobName}</p>
                      {#if bill.type === 'job'}
                        <p class="text-sm text-blue-600">Job Mode - Cost Groups and Items Available</p>
                      {:else}
                        <p>PO #{bill.vendorOrderNumber} - {bill.vendorName}</p>
                      {/if}
                    </div>
                    <button 
                      on:click={() => removeVendorBill(bill.id)}
                      class="text-red-500 hover:text-red-700 text-xl font-bold border-0"
                      title="Remove this {bill.type === 'job' ? 'job' : 'vendor bill'}"
                    >
                      ×
                    </button>
                  </div>
                </div>
              {/each}
            </div>
          {/if}
          
          <!-- Inline Add Form: Show automatically when no matches, or when toggled with existing matches -->
          {#if matchedVendorBills.length === 0 || showInlineAddForm}
            <div class="border-t border-gray-200 pt-3 {matchedVendorBills.length > 0 ? 'mt-2' : ''}">
              <!-- Mode Toggle Switch -->
              <div class="flex items-center justify-center mb-3 p-2 bg-gray-50 rounded-md border">
                <span class="text-sm font-medium text-gray-700 mr-3">Match on:</span>
                <div class="flex items-center space-x-3">
                  <span class="text-sm font-medium {!isJobMode ? 'text-blue-600' : 'text-gray-500'}">PO</span>
                  <label class="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      bind:checked={isJobMode}
                      on:change={() => {
                        fetch('/api/users/bill-settings', {
                          method: 'PATCH',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ billmodedefaultjob: isJobMode })
                        }).then(() => console.log('Bill mode default saved:', isJobMode ? 'Job' : 'PO'))
                          .catch(err => console.error('Failed to save bill mode default:', err));
                      }}
                      class="sr-only peer"
                    />
                    <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                  <span class="text-sm font-medium {isJobMode ? 'text-blue-600' : 'text-gray-500'}">Job</span>
                </div>
              </div>
            
              <div class="mb-2">
                <label for="inline-customer-search" class="block text-sm font-medium text-gray-700 mb-1">{isJobMode ? 'Job' : 'Customer'}</label>
                <div class="relative">
                  <input
                    type="text"
                    id="inline-customer-search"
                    placeholder="{isJobMode ? 'Type to search for a job...' : 'Type to search for a customer...'}"
                    class="block w-full pl-3 pr-10 py-2 text-base {!selectedCustomer ? 'border-2 border-orange-500' : 'border border-gray-300'} focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md placeholder-gray-500"
                    autocomplete="off"
                    bind:value={customerSearchQuery}
                    bind:this={customerInputElement}
                    on:input={handleCustomerSearch}
                    on:focus={() => { 
                      showCustomerDropdown = true; 
                      filterCustomers(); // Ensure filtered list is populated when opening
                    }}
                    on:blur={() => handleInputFocusOut('customer')}
                  />
                  
                  {#if showCustomerDropdown}
                    <div 
                      class="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm"
                      on:wheel={(e) => e.stopPropagation()}
                    >
                      {#if filteredCustomers.length === 0}
                        <div class="text-gray-500 px-4 py-2">{isJobMode ? 'No jobs found' : 'No customers found'}</div>
                      {:else}
                        {#each filteredCustomers as customer}
                          <div 
                            role="button"
                            tabindex="0"
                            class="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-blue-500 hover:text-white {customer.id === selectedCustomer ? 'bg-blue-100' : ''}"
                            on:mousedown|preventDefault={() => selectFilteredCustomer(customer.id)}
                            on:keydown={(e) => e.key === 'Enter' && selectFilteredCustomer(customer.id)}
                          >
                            {customer.name}
                          </div>
                        {/each}
                      {/if}
                    </div>
                  {/if}
                </div>
              </div>
            
              {#if !isJobMode}
              <div class="mb-3">
                <label for="inline-vendor-order-search" class="block text-sm font-medium text-gray-700 mb-1">Vendor Order</label>
                <div class="relative">
                  {#if isLoadingVendorOrders}
                    <div class="border border-gray-300 rounded-md p-2 flex items-center justify-center">
                      <div class="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                      <span class="ml-2">Loading vendor orders...</span>
                    </div>
                  {:else}
                    <input
                      type="text"
                      id="inline-vendor-order-search"
                      placeholder="Type to search for a vendor order..."
                      class="block w-full pl-3 pr-10 py-2 text-base {selectedCustomer && !selectedVendorOrder ? 'border-2 border-orange-500' : 'border border-gray-300'} focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md placeholder-gray-500"
                      autocomplete="off"
                      bind:value={vendorOrderSearchQuery}
                      bind:this={vendorOrderInputElement}
                      on:input={handleVendorOrderSearch}
                      on:focus={() => showVendorOrdersList()}
                      on:blur={() => handleInputFocusOut('vendorOrder')}
                    />
                    
                    {#if showVendorOrderDropdown}
                      <div 
                        class="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm"
                        on:wheel={(e) => e.stopPropagation()}
                      >
                        {#if filteredVendorOrders.length === 0}
                          <div class="text-gray-500 px-4 py-2">No vendor orders found</div>
                        {:else}
                          {#each filteredVendorOrders as order}
                            <div 
                              role="button"
                              tabindex="0"
                              class="group cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-blue-500 hover:text-white {order.id === selectedVendorOrder ? 'bg-blue-100' : ''}"
                              on:click={() => selectFilteredVendorOrder(order.id)}
                              on:keydown={(e) => e.key === 'Enter' && selectFilteredVendorOrder(order.id)}
                            >
                              <div class="flex flex-col">
                                <div class="flex items-center justify-between">
                                  <span class="font-medium block truncate">
                                    PO #{order.number}
                                  </span>
                                  <span class="ml-2 text-sm text-gray-500 group-hover:text-white">
                                    {new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(order.cost || 0)}
                                  </span>
                                </div>
                                {#if order.subject}
                                  <span class="text-sm text-gray-500 group-hover:text-white truncate">
                                    {order.subject.substring(0, 50)}
                                  </span>
                                {/if}
                              </div>
                            </div>
                          {/each}
                        {/if}
                      </div>
                    {/if}
                  {/if}
                </div>
              </div>
              {/if}
            </div>
          {/if}
          
          <!-- Job Cost Groups Cache Status & Refresh - INSIDE the matched bills box -->
          {#if isJobMode && matchedVendorBills.length > 0 && !isLoadingCostGroups && lineItemsData?.costGroups && lineItemsData.costGroups.length > 0}
            <div class="flex items-center justify-between mt-3 px-2 py-2 bg-gray-50 border border-gray-200 rounded">
              <div class="text-sm">
                {#if costGroupsCached}
                  <span class="text-green-700 font-medium">✓ Using cached cost groups</span>
                  {#if costGroupsCachedAt}
                    <span class="text-gray-500 ml-2 text-xs">
                      (loaded {new Date(costGroupsCachedAt).toLocaleString()})
                    </span>
                  {/if}
                {:else}
                  <span class="text-blue-700 font-medium">Fresh data loaded from JobTread</span>
                {/if}
              </div>
              <button
                on:click={refreshCostGroups}
                disabled={isLoadingCostGroups}
                class="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded hover:bg-[#f0f4ff] 
                       disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 
                       transition-colors duration-150"
                title="Fetch fresh cost groups from JobTread"
              >
                <svg 
                  class="w-4 h-4 {isLoadingCostGroups ? 'animate-spin' : ''}" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    stroke-linecap="round" 
                    stroke-linejoin="round" 
                    stroke-width="2" 
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
                  />
                </svg>
                {isLoadingCostGroups ? 'Refreshing...' : 'Refresh Cost Groups'}
              </button>
            </div>
          {/if}
          
          <!-- Loading Indicator - Reserve fixed space to prevent layout jumping -->
          <div class="mt-3 min-h-[80px] flex items-center justify-center">
            {#if isLoadingCostGroups}
              <div class="p-4 bg-blue-50 border border-blue-200 rounded-md inline-flex items-center">
                <svg class="animate-spin h-5 w-5 text-blue-600 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span class="text-blue-700 font-medium">{isJobMode ? 'Loading cost groups and caching this job...' : 'Loading line items...'}</span>
              </div>
            {/if}
          </div>
        </div>
      </div>

      <!-- Assignment Mode Toggle -->
      {@const canToggleMode = matchedVendorBills.length > 0}
      <div class="mt-4 mb-4 flex flex-col items-center justify-center">
        <div class="inline-flex rounded-lg border border-gray-300 p-1 {canToggleMode ? 'bg-gray-100' : 'bg-gray-200 opacity-60'}">
          <button
            disabled={!canToggleMode}
            class="px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 {!useSplitMode ? 'bg-blue-500 text-white shadow-sm' : 'text-gray-700 hover:text-gray-900'} disabled:cursor-not-allowed disabled:opacity-70"
            on:click={() => {
              if (canToggleMode && useSplitMode) {
                useSplitMode = false;
                handleSplitModeToggle();
              }
            }}
          >
            Use Lines from Xero
          </button>
          <button
            disabled={!canToggleMode}
            class="px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 {useSplitMode ? 'bg-blue-500 text-white shadow-sm' : 'text-gray-700 hover:text-gray-900'} disabled:cursor-not-allowed disabled:opacity-70"
            on:click={() => {
              if (canToggleMode && !useSplitMode) {
                useSplitMode = true;
                handleSplitModeToggle();
              }
            }}
          >
            Split Invoice Value to Cost Group
          </button>
        </div>
        {#if !canToggleMode}
          <p class="text-xs text-gray-500 mt-2">Add a job or PO to the matching section above to enable split mode</p>
        {/if}
      </div>

      <!-- Amount Selector (for partial payments) -->
      {#if hasPartialPayments()}
        {@const amountPaid = currentBill.total - currentBill.amountDue}
        <div class="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h4 class="font-medium text-gray-900 mb-2">Select Amount to {useSplitMode ? 'Split' : 'Assign'}</h4>
          <p class="text-sm text-gray-600 mb-3">
            This invoice has a partial payment of {new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(amountPaid)}.
            Choose which amount to {useSplitMode ? 'split' : 'work with'}:
          </p>
          <div class="space-y-2">
            <label class="flex items-center cursor-pointer">
              <input 
                type="radio" 
                bind:group={useAmountDue} 
                value={true}
                on:change={() => {
                  if (useSplitMode && selectedCostGroupForSplit) {
                    calculateSplitAssignments(selectedCostGroupForSplit);
                  }
                }}
                class="w-4 h-4 text-blue-600"
              />
              <span class="ml-2">
                <strong>Amount Due:</strong> {new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(currentBill.amountDue)}
                <span class="text-gray-600 text-sm ml-1">(remaining balance)</span>
              </span>
            </label>
            <label class="flex items-center cursor-pointer">
              <input 
                type="radio" 
                bind:group={useAmountDue} 
                value={false}
                on:change={() => {
                  if (useSplitMode && selectedCostGroupForSplit) {
                    calculateSplitAssignments(selectedCostGroupForSplit);
                  }
                }}
                class="w-4 h-4 text-blue-600"
              />
              <span class="ml-2">
                <strong>Full Amount:</strong> {new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(currentBill.total)}
                <span class="text-gray-600 text-sm ml-1">(original invoice total)</span>
              </span>
            </label>
          </div>
        </div>
      {/if}

      <!-- Split Mode: Invoice Total Display -->
      {#if useSplitMode}
        {@const effectiveAmount = getEffectiveInvoiceAmount()}
        {@const amountType = useAmountDue && hasPartialPayments() ? 'Amount Due' : 'Full Amount'}
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <p class="text-lg font-medium text-blue-900">
            The amount to split is {new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(effectiveAmount)}
            {#if hasPartialPayments()}
              <span class="text-sm font-normal">({amountType})</span>
            {/if}
          </p>
          <p class="text-sm text-blue-700 mt-1">
            Select a Cost Group below to split the value across its items proportionally.
          </p>
        </div>
      {/if}

      {#if useSplitMode}
        <!-- Split Mode: Cost Groups Table -->
        <h3 class="text-lg font-normal mt-3 mb-2">Cost Groups</h3>
        {@const availableCostGroups = getAvailableCostGroups()}
        
        {#if availableCostGroups.length === 0}
          <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <p class="text-yellow-800">No cost groups available. Please add a job or PO to the matching section above.</p>
          </div>
        {:else}
          <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <!-- Header Row -->
            <div class="flex items-center px-4 py-2 border-b border-gray-200 bg-gray-50">
              <div class="mr-6">
                <span class="text-xs font-semibold text-gray-700 uppercase tracking-wider">Select</span>
              </div>
              <div class="mr-3">
                <span class="text-xs font-semibold text-gray-700 uppercase tracking-wider">Cost Group Name in Budget</span>
              </div>
              <div class="flex-1"></div>
            </div>
            
            {#each availableCostGroups as costGroup, groupIndex}
              {@const isExpanded = expandedCostGroups[costGroup.id]}
              {@const isSelected = selectedCostGroupForSplit?.id === costGroup.id}
              {@const groupTotal = costGroup.lineItems?.reduce((sum, item) => sum + (item.cost || 0), 0) || 0}
              {@const canSelect = groupTotal > 0}
              
              <!-- Cost Group Row -->
              <div class="border-b border-gray-200 {isSelected ? 'bg-green-50' : groupIndex % 2 === 0 ? 'bg-gray-50' : 'bg-white'}">
                <div 
                  class="px-4 py-3 flex items-center cursor-pointer hover:bg-gray-100 transition-colors"
                  role="button"
                  tabindex="0"
                  on:click={() => {
                    if (canSelect) {
                      handleCostGroupSelection(costGroup);
                    }
                  }}
                  on:keydown={(e) => {
                    if (e.key === 'Enter' && canSelect) {
                      handleCostGroupSelection(costGroup);
                    }
                  }}
                >
                  <!-- Checkbox -->
                  <div class="mr-3" role="presentation">
                    <input
                      type="radio"
                      name="costGroupSelection"
                      checked={isSelected}
                      disabled={!canSelect}
                      class="w-4 h-4 text-blue-600 disabled:opacity-50 cursor-pointer pointer-events-none"
                    />
                  </div>
                  
                  <!-- Expand/Collapse Icon -->
                  <div class="ml-3 mr-3 text-gray-500">
                    {#if isExpanded}
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    {:else}
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                      </svg>
                    {/if}
                  </div>
                  
                  <!-- Cost Group Name and Total -->
                  <div class="flex-1">
                    <span class="font-semibold text-gray-900">{cleanCostGroupName(costGroup.name) || `Cost Group ${groupIndex + 1}`}</span>
                    <span class="ml-3 text-sm text-gray-600">
                      Budget: {new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(groupTotal)}
                    </span>
                    {#if costGroup.lineItems?.length > 0}
                      <span class="ml-2 text-xs text-gray-500">
                        ({costGroup.lineItems.length} {costGroup.lineItems.length === 1 ? 'item' : 'items'})
                      </span>
                    {/if}
                  </div>
                  
                  {#if isSelected}
                    <span class="ml-2 px-2 py-1 bg-green-600 text-white text-xs rounded-full">Selected</span>
                  {/if}
                </div>
                
                <!-- Expanded Items -->
                {#if isExpanded && costGroup.lineItems && costGroup.lineItems.length > 0}
                  <div class="bg-white border-t border-gray-200">
                    <table class="min-w-full text-sm">
                      <thead class="bg-white">
                        <tr>
                          <th class="px-6 py-2 text-left text-xs font-medium text-gray-400 uppercase">Item Name</th>
                          <th class="px-6 py-2 text-right text-xs font-medium text-gray-400 uppercase">Budget Amount</th>
                          {#if isSelected}
                            <th class="px-6 py-2 text-right text-xs font-medium text-gray-400 uppercase">Split %</th>
                            <th class="px-6 py-2 text-right text-xs font-medium text-gray-400 uppercase">Split Amount</th>
                          {/if}
                        </tr>
                      </thead>
                      <tbody>
                        {#each costGroup.lineItems as item, itemIndex}
                          {@const splitAssignment = isSelected ? splitAssignments.find(a => a.originalItem.id === item.id) : null}
                          {@const splitPercentage = splitAssignment ? (splitAssignment.splitRatio * 100).toFixed(1) : 0}
                          {@const hasGSTDisplay = splitAssignment?.hasGST}
                          {@const exclusiveAmount = splitAssignment?.invoiceLineItem.assignedCost || 0}
                          {@const inclusiveAmount = hasGSTDisplay ? exclusiveAmount + (Number(splitAssignment?.invoiceLineItem.taxAmount) || 0) : exclusiveAmount}
                          <tr class="{itemIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}">
                            <td class="px-6 py-2 text-gray-900">
                              {item.name || 'Unnamed Item'}{#if item.costCode} <span class="text-gray-600">({item.costCode.number} {item.costCode.name})</span>{/if}
                            </td>
                            <td class="px-6 py-2 text-right text-gray-700">
                              {new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(item.cost || 0)}
                            </td>
                            {#if isSelected && splitAssignment}
                              <td class="px-6 py-2 text-right text-blue-600 font-medium">{splitPercentage}%</td>
                              <td class="px-6 py-2 text-right">
                                <div class="text-green-600 font-semibold">
                                  {new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(exclusiveAmount)}
                                </div>
                                {#if hasGSTDisplay}
                                  <div class="text-xs text-gray-600 mt-0.5">
                                    (Incl. {taxConfig.name}: {new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(inclusiveAmount)})
                                  </div>
                                {/if}
                              </td>
                            {/if}
                          </tr>
                        {/each}
                      </tbody>
                    </table>
                  </div>
                {/if}
              </div>
            {/each}
          </div>
        {/if}
      {/if}

      {#if !useSplitMode}
        <!-- Original Line Items Table (Normal Mode) -->
        <div class="flex items-center justify-between mt-3 mb-2 gap-4 flex-wrap">
          <h3 class="text-lg font-normal">Line Items from Xero</h3>

          <div class="flex items-center gap-4 flex-wrap">
            <!-- Assignment lens toggle: Cost Group vs Cost Code -->
            <div class="inline-flex items-center rounded-md border border-gray-300 overflow-hidden text-sm">
              <span class="px-2 py-1 text-xs text-gray-500 bg-gray-50 border-r border-gray-300">Search by</span>
              <button
                type="button"
                on:click={() => activeLens = 'costGroup'}
                class="px-3 py-1 transition-colors {activeLens === 'costGroup' ? 'bg-blue-500 text-white font-semibold' : 'bg-white text-gray-700 hover:bg-gray-50'}"
              >
                Cost Group
              </button>
              <button
                type="button"
                on:click={() => activeLens = 'costCode'}
                class="px-3 py-1 border-l border-gray-300 transition-colors {activeLens === 'costCode' ? 'bg-blue-500 text-white font-semibold' : 'bg-white text-gray-700 hover:bg-gray-50'}"
              >
                Cost Code
              </button>
            </div>

            {#if isJobMode && lineItemsData?.costGroups && lineItemsData.costGroups.length > 0}
              <span class="text-xs text-gray-500">{lineItemsData.costGroups.reduce((sum, g) => sum + (g.lineItems?.length || 0), 0)} budget items</span>
            {/if}
          </div>
        </div>
      {#if currentBill.lineItems && currentBill.lineItems.length > 0}
        <div class="overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead class="sticky top-0 z-10">
              <tr>
                <th class="px-3 py-2 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-400 uppercase tracking-wider">Line Item</th>
                <th class="px-3 py-2 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-400 uppercase tracking-wider">Cost</th>
                <th class="px-3 py-2 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-400 uppercase tracking-wider w-16">Qty</th>
                <th class="px-3 py-2 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-400 uppercase tracking-wider">Total</th>
                <th class="px-3 py-2 border-b border-gray-200 bg-gray-50 text-center text-xs leading-4 font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">Job Name</th>
                <th class="px-3 py-2 border-b border-gray-200 bg-gray-50 text-center text-xs leading-4 font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">Jobtread PO</th>
                <th class="px-3 py-2 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-400 uppercase tracking-wider">{isJobMode ? 'Job' : 'Vendor Bill'}</th>
                <th class="px-3 py-2 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-400 uppercase tracking-wider {isJobMode ? 'w-64' : ''}">Assignment</th>
                <th class="px-3 py-2 border-b border-gray-200 bg-gray-50 text-right text-xs leading-4 font-medium text-gray-400 uppercase tracking-wider">Assigned Cost</th>
              </tr>
            </thead>
            <tbody class="bg-white">
              {#each (currentBill.lineItems || []).filter(li => li.unitAmount != null && li.unitAmount !== 0 && !isNaN(li.unitAmount)) as lineItem}
                <tr>
                  <td class="px-3 py-2 whitespace-no-wrap border-b border-gray-100">{lineItem.description ?? '—'}</td>
                  <td class="px-3 py-2 whitespace-no-wrap border-b border-gray-100">
                    {#if caLineSplits?.[lineItem.lineItemID]?.nonRecoverable}
                      <!-- Canada with PST: the cost JobTread records (pre-tax + PST); recoverable tax stays out -->
                      {new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(caLineSplits[lineItem.lineItemID].jobCost / (Number(lineItem.quantity) || 1))}
                    {:else if currentBill.lineAmountTypes === 'Exclusive' && hasGST(lineItem)}
                      {new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format((lineItem.unitAmount ?? 0) + (Number(lineItem.taxAmount) || 0) / (Number(lineItem.quantity) || 1))}
                    {:else}
                      {new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(lineItem.unitAmount ?? 0)}
                    {/if}
                  </td>
                  <td class="px-3 py-2 whitespace-no-wrap border-b border-gray-100 w-16">{lineItem.quantity || 1}</td>
                  <td class="px-3 py-2 whitespace-no-wrap border-b border-gray-100">
                    {#if caLineSplits?.[lineItem.lineItemID]?.nonRecoverable}
                      {@const split = caLineSplits[lineItem.lineItemID]}
                      {new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(split.jobCost)}
                      {#if split.nonRecoverable || split.recoverable}
                        <div class="text-[11px] text-gray-500">{#if split.nonRecoverable}incl. PST {new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(split.nonRecoverable)}{/if}{#if split.nonRecoverable && split.recoverable} · {/if}{#if split.recoverable}+ {taxConfig.name} {new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(split.recoverable)}{/if}</div>
                      {/if}
                    {:else if currentBill.lineAmountTypes === 'Exclusive' && hasGST(lineItem)}
                      {new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(lineItem.lineAmount + lineItem.taxAmount)}
                    {:else}
                      {new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(lineItem.lineAmount)}
                    {/if}
                  </td>
                  <td class="px-3 py-2 whitespace-no-wrap border-b border-gray-100 text-center">
                    {#if lineItem.tracking && lineItem.tracking.length > 0}
                      {@const jobVal = getJobValueFromLineItems([lineItem], billTrackingSettings, 'jtBillNameField')}
                      {#if jobVal}
                        <span class="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 whitespace-nowrap">{jobVal}</span>
                      {/if}
                    {/if}
                  </td>
                  <td class="px-3 py-2 whitespace-no-wrap border-b border-gray-100 text-center">
                    {#if lineItem.tracking && lineItem.tracking.length > 0}
                      {@const poVal = getPOValueFromLineItems([lineItem])}
                      {#if poVal}
                        <span class="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 whitespace-nowrap">{poVal}</span>
                      {/if}
                    {/if}
                  </td>
                  <td class="px-3 py-2 whitespace-no-wrap border-b border-gray-100">
                    <select 
                      class="w-full p-1 rounded text-sm border border-gray-300 {isJobMode ? (hasJobInMatching ? 'bg-gray-100 text-gray-700 cursor-not-allowed' : 'bg-gray-100 text-gray-400 cursor-not-allowed') : hasSinglePOInMatching ? 'bg-gray-100 text-gray-700 cursor-not-allowed' : 'bg-white'}" 
                      on:change={(event) => selectVendorBillForLineItem(lineItem.lineItemID, event.target.value)}
                      value={lineItemVendorBillSelections[lineItem.lineItemID] || ''}
                      disabled={isJobMode || hasSinglePOInMatching}
                    >
                      <option value="">{isJobMode ? (hasJobInMatching ? 'Select Job' : 'Select job above first') : 'Select Vendor Bill'}</option>
                      {#if isJobMode}
                        {#each customers as job}
                          <option value={job.id}>
                            {job.name}
                          </option>
                        {/each}
                      {:else}
                        {#each matchedVendorBills.filter(b => b.vendorOrderNumber) as bill}
                          <option value={bill.id}>
                            {bill.jobName} - PO #{bill.vendorOrderNumber}
                          </option>
                        {/each}
                      {/if}
                    </select>
                  </td>
                  <td class="px-3 py-2 whitespace-no-wrap border-b border-gray-100">
                    {#if isJobMode}
                      <!-- Job Mode: Assignment to cost group items with search -->
                      <div class="relative">
                        <input
                          type="text"
                          placeholder={!hasJobInMatching ? 'Select job above first' : (getAssignmentDisplayText(lineItem) || 'Type to search Assignment')}
                          class="w-full p-1 rounded text-sm {!hasJobInMatching ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : getAssignmentValidationClass(lineItem)} {hasJobInMatching ? 'bg-white' : ''} placeholder-gray-500"
                          disabled={!hasJobInMatching || !lineItemVendorBillSelections[lineItem.lineItemID] || !lineItemsData?.costGroups}
                          bind:value={assignmentSearchQueries[lineItem.lineItemID]}
                          on:input={(e) => handleAssignmentSearch(lineItem.lineItemID, e.target.value)}
                          on:focus={(e) => { openAssignmentDropdown(lineItem.lineItemID, e.target); e.target.select(); }}
                          on:blur={() => handleAssignmentInputFocusOut(lineItem.lineItemID)}
                          autocomplete="off"
                        />
                        
                        {#if assignmentDropdownOpen[lineItem.lineItemID] && lineItemVendorBillSelections[lineItem.lineItemID] && lineItemsData?.costGroups}
                          {@const position = assignmentDropdownPositions[lineItem.lineItemID]}
                          {#key assignmentSearchQueries[lineItem.lineItemID]}
                            <div 
                              class="fixed z-50 bg-white shadow-lg max-h-96 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm"
                              style="top: {position?.top || 0}px; left: {position?.left || 0}px; width: {position?.width || 0}px;"
                              on:wheel={(e) => e.stopPropagation()}
                              on:mousedown={(e) => e.preventDefault()}
                            >
                            {#if getAssignmentBucketsByActiveLens(lineItem.lineItemID).length === 0}
                              <div class="text-gray-500 px-4 py-2">No items found</div>
                            {:else}
                              {#each getAssignmentBucketsByActiveLens(lineItem.lineItemID) as bucket, bucketIndex}
                                {#if bucket.lineItems.length > 0}
                                  <div class="px-3 py-1 text-xs font-semibold text-gray-500 bg-gray-50">
                                    {#if activeLens === 'costCode'}
                                      {bucket.name}
                                    {:else}
                                      {#if bucket.parentCostGroup?.parentCostGroup?.name}<span class="font-normal text-gray-400">{cleanCostGroupName(bucket.parentCostGroup.parentCostGroup.name)} &#8250; </span>{/if}{#if bucket.parentCostGroup?.name}<span class="font-normal">{cleanCostGroupName(bucket.parentCostGroup.name)} &#8250; </span>{/if}{cleanCostGroupName(bucket.name) || `Group ${bucketIndex + 1}`}
                                    {/if}
                                  </div>
                                  {#each bucket.lineItems as item}
                                    {@const originalGroupIndex = activeLens === 'costCode' ? item._originalGroupIndex : lineItemsData.costGroups.findIndex(g => g.id === bucket.id)}
                                    <div
                                      role="button"
                                      tabindex="0"
                                      class="group cursor-pointer select-none relative py-2 pl-6 pr-3 hover:bg-blue-500 hover:text-white text-sm"
                                      on:mousedown={() => selectAssignmentItem(lineItem, originalGroupIndex, item.id)}
                                      on:keydown={(e) => e.key === 'Enter' && selectAssignmentItem(lineItem, originalGroupIndex, item.id)}
                                    >
                                      <div class="flex flex-col">
                                        <span class="font-medium">
                                          {#if activeLens === 'costCode' && item.costCode?.number}<span class="text-xs font-mono text-gray-400 group-hover:text-blue-100 mr-1">{item.costCode.number}</span>{/if}{item.name}
                                        </span>
                                        <span class="text-xs text-gray-500 group-hover:text-white">
                                          {#if activeLens === 'costCode'}
                                            {#if item.costGroup?.name}in: {#if item.costGroup.parentCostGroup?.parentCostGroup?.name}{cleanCostGroupName(item.costGroup.parentCostGroup.parentCostGroup.name)} &#8250; {/if}{#if item.costGroup.parentCostGroup?.name}{cleanCostGroupName(item.costGroup.parentCostGroup.name)} &#8250; {/if}{cleanCostGroupName(item.costGroup.name)} · {/if}{new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(item.cost || 0)}
                                          {:else}
                                            {#if item.costCode}({item.costCode.number} {item.costCode.name}) - {/if}{new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(item.cost || 0)}
                                          {/if}
                                        </span>
                                      </div>
                                    </div>
                                  {/each}
                                {/if}
                              {/each}
                            {/if}
                          </div>
                          {/key}
                        {/if}
                      </div>
                    {:else}
                      <!-- PO Mode: Assignment to PO items with search -->
                      {#if lineItemVendorBillSelections[lineItem.lineItemID]}
                        {@const selectedBill = matchedVendorBills.find(bill => bill.id === lineItemVendorBillSelections[lineItem.lineItemID])}
                        <div class="relative">
                          <input
                            type="text"
                            placeholder={getAssignmentDisplayText(lineItem) || "Search to assign..."}
                            class="w-full p-1 rounded text-sm {getAssignmentValidationClass(lineItem)} bg-white placeholder-gray-500"
                            disabled={!lineItemVendorBillSelections[lineItem.lineItemID] || !selectedBill?.lineItems}
                            bind:value={assignmentSearchQueries[lineItem.lineItemID]}
                            on:input={(e) => handleAssignmentSearch(lineItem.lineItemID, e.target.value)}
                            on:focus={(e) => { openAssignmentDropdown(lineItem.lineItemID, e.target); e.target.select(); }}
                            on:blur={() => handleAssignmentInputFocusOut(lineItem.lineItemID)}
                            autocomplete="off"
                          />
                          
                          {#if assignmentDropdownOpen[lineItem.lineItemID] && selectedBill?.lineItems?.costGroups}
                            {@const position = assignmentDropdownPositions[lineItem.lineItemID]}
                            {#key assignmentSearchQueries[lineItem.lineItemID]}
                              <div 
                                class="fixed z-50 bg-white shadow-lg max-h-96 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm"
                                style="top: {position?.top || 0}px; left: {position?.left || 0}px; width: {position?.width || 0}px;"
                                on:wheel={(e) => e.stopPropagation()}
                                on:mousedown={(e) => e.preventDefault()}
                              >
                              {#if getAssignmentBucketsByActiveLens(lineItem.lineItemID).length === 0}
                                <div class="text-gray-500 px-4 py-2">No items found</div>
                              {:else}
                                {#each getAssignmentBucketsByActiveLens(lineItem.lineItemID) as bucket, bucketIndex}
                                  {#if bucket.lineItems.length > 0}
                                    <div class="px-3 py-1 text-xs font-semibold text-gray-500 bg-gray-50">
                                      {#if activeLens === 'costCode'}
                                        {bucket.name}
                                      {:else}
                                        {#if bucket.parentCostGroup?.parentCostGroup?.name}<span class="font-normal text-gray-400">{cleanCostGroupName(bucket.parentCostGroup.parentCostGroup.name)} &#8250; </span>{/if}{#if bucket.parentCostGroup?.name}<span class="font-normal">{cleanCostGroupName(bucket.parentCostGroup.name)} &#8250; </span>{/if}{cleanCostGroupName(bucket.name) || `Group ${bucketIndex + 1}`}
                                      {/if}
                                    </div>
                                    {#each bucket.lineItems as item}
                                      {@const originalGroupIndex = activeLens === 'costCode' ? item._originalGroupIndex : selectedBill.lineItems.costGroups.findIndex(g => g.id === bucket.id)}
                                      <div
                                        role="button"
                                        tabindex="0"
                                        class="group cursor-pointer select-none relative py-2 pl-6 pr-3 hover:bg-blue-500 hover:text-white text-sm"
                                        on:mousedown={() => selectAssignmentItem(lineItem, originalGroupIndex, item.id)}
                                        on:keydown={(e) => e.key === 'Enter' && selectAssignmentItem(lineItem, originalGroupIndex, item.id)}
                                      >
                                        <div class="flex flex-col">
                                          <span class="font-medium">
                                            {#if activeLens === 'costCode' && item.costCode?.number}<span class="text-xs font-mono text-gray-400 group-hover:text-blue-100 mr-1">{item.costCode.number}</span>{/if}{item.name}
                                          </span>
                                          <span class="text-xs text-gray-500 group-hover:text-white">
                                            {#if activeLens === 'costCode'}
                                              {#if item.costGroup?.name}in: {#if item.costGroup.parentCostGroup?.parentCostGroup?.name}{cleanCostGroupName(item.costGroup.parentCostGroup.parentCostGroup.name)} &#8250; {/if}{#if item.costGroup.parentCostGroup?.name}{cleanCostGroupName(item.costGroup.parentCostGroup.name)} &#8250; {/if}{cleanCostGroupName(item.costGroup.name)} · {/if}{new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(item.cost || 0)}
                                            {:else}
                                              {#if item.costCode}({item.costCode.number} {item.costCode.name}) - {/if}{new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(item.cost || 0)}
                                            {/if}
                                          </span>
                                        </div>
                                      </div>
                                    {/each}
                                  {/if}
                                {/each}
                              {/if}
                            </div>
                            {/key}
                          {/if}
                        </div>
                      {:else}
                        <div class="relative">
                          <input
                            type="text"
                            placeholder="Search to assign..."
                            class="w-full p-1 rounded text-sm border border-gray-300 text-gray-500 bg-white placeholder-gray-500"
                            disabled={true}
                            autocomplete="off"
                          />
                        </div>
                      {/if}
                    {/if}
                  </td>
                  <td class="px-3 py-2 whitespace-no-wrap border-b border-gray-100 text-right">
                    {#if assignedItems.some(item => item.invoiceLineItem.lineItemID === lineItem.lineItemID)}
                      {@const assignedItem = assignedItems.find(item => item.invoiceLineItem.lineItemID === lineItem.lineItemID)}
                      <span class="text-green-600">
                        ✓ {new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(
                          assignedItem ? assignedItem.invoiceLineItem.assignedCost + (caLineSplits?.[lineItem.lineItemID]?.nonRecoverable || 0) : 0
                        )}
                      </span>
                    {:else}
                      Not assigned
                    {/if}
                  </td>
                </tr>
              {/each}
            </tbody>
            <tfoot>
              <tr>
                <td colspan="7" class="border-t border-gray-300"></td>
                <td class="border-t border-gray-300 px-3 py-2 text-right font-bold whitespace-nowrap">
                  Total Assigned: {new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(displayTotalAssigned)}{#if caBillPst} <span class="text-xs font-normal text-gray-500">(incl. PST)</span>{/if}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      {:else}
        <p class="text-red-500">No line items found for this bill.</p>
      {/if}
      {/if}
      <!-- End of Normal Mode -->
      {/if}
    </div>
    <!-- End Scrollable Content Area -->

    <!-- Sticky Button Footer -->
    {#if currentBill && (!showVendorMatching || vendorBillCreated)}
      <div class="sticky-footer bg-white border-t border-gray-200 px-4 py-3 flex justify-end shadow-lg">
        <div class="flex items-center gap-3 w-full">
          {#if vendorBillCreated}
            <div class="flex flex-col w-full">
              <span class="text-green-600 font-medium flex items-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
                {#if vendorBillsCreationResults.length > 1}
                  {vendorBillsCreationResults.filter(r => r.success).length}/{vendorBillsCreationResults.length} vendor bills created successfully!
                {:else}
                  Vendor bill created successfully!
                {/if}
              </span>
              
              {#if vendorBillsCreationResults.length > 0}
                <div class="w-full border rounded-md border-gray-200 overflow-hidden">
                  <table class="min-w-full divide-y divide-[#edf0f7]">
                    <thead class="bg-white">
                      <tr>
                        <th scope="col" class="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">PO Number</th>
                        <th scope="col" class="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                        <th scope="col" class="px-3 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-[#edf0f7]">
                      {#each vendorBillsCreationResults as result}
                        <tr class="{result.success ? 'bg-green-50' : 'bg-red-50'}">
                          <td class="px-3 py-2 whitespace-nowrap text-sm font-medium">
                            PO #{result.vendorOrderNumber || 'Unknown'}
                          </td>
                          <td class="px-3 py-2 whitespace-nowrap text-sm">
                            {#if result.success}
                              <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                <svg class="mr-1.5 h-2 w-2 text-green-400" fill="currentColor" viewBox="0 0 8 8">
                                  <circle cx="4" cy="4" r="3" />
                                </svg>
                                Created
                              </span>
                            {:else}
                              <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                <svg class="mr-1.5 h-2 w-2 text-red-400" fill="currentColor" viewBox="0 0 8 8">
                                  <circle cx="4" cy="4" r="3" />
                                </svg>
                                Failed
                              </span>
                              {#if result.error}
                                <span class="block text-xs text-red-600 mt-1">{result.error}</span>
                              {/if}
                            {/if}
                          </td>
                          <td class="px-3 py-2 whitespace-nowrap text-right text-sm font-medium">
                            {#if result.success && result.url}
                              <a 
                                href={result.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                class="bg-blue-500 hover:bg-blue-600 text-white text-xs px-3 py-1 rounded transition-colors duration-200"
                              >
                                Open in JobTread →
                              </a>
                            {/if}
                          </td>
                        </tr>
                      {/each}
                    </tbody>
                  </table>
                </div>
              {/if}
            </div>
          {/if}
          
          <div class="flex items-center gap-3 ml-auto">
            {#if !vendorBillCreated}
              <button 
                on:click={() => { if (currentBillIndex + 1 >= selectedBills.length) { navigate('/import-bills-from-xero'); } else { nextBill(); } }}
                class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded text-sm"
              >
                {currentBillIndex + 1 >= selectedBills.length ? 'Skip & Finish' : `Skip Bill ${currentBillIndex + 1}/${selectedBills.length}`}
              </button>
              
              <button 
                on:click={confirmAndPrepareVendorBill}
                disabled={!allItemsAssigned || isCreatingVendorBill}
                class="font-bold py-2 px-4 rounded text-sm flex items-center 
                       {(!allItemsAssigned || isCreatingVendorBill) 
                         ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                         : 'bg-emerald-500 hover:bg-emerald-600 text-white'
                       }
                       {isCreatingVendorBill ? 'opacity-75' : ''}"
              >
                {#if isCreatingVendorBill}
                  <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {#if processingVendorBills}
                    Creating bills ({vendorBillsProcessed}/{totalVendorBillsToProcess})...
                  {:else}
                    Creating...
                  {/if}
                {:else}
                  {allItemsAssigned ? 'Confirm Vendor' : 'Assign All Line Items'}
                {/if}
              </button>
            {/if}
            
            {#if vendorBillCreated}
              {#if currentBillIndex + 1 >= selectedBills.length}
                <button 
                  on:click={() => navigate('/import-bills-from-xero')}
                  class="bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-2 px-4 rounded text-sm whitespace-nowrap"
                >
                  Done — Back to Bills
                </button>
              {:else}
                <button 
                  on:click={nextBill}
                  class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded text-sm whitespace-nowrap"
                >
                  Next Bill {currentBillIndex + 1}/{selectedBills.length}
                </button>
              {/if}
            {/if}
          </div>
        </div>
      </div>
    {/if}
    <!-- End Sticky Button Footer -->


    {#if showVendorMatching && !vendorBillCreated}
      <div id="vendor-matching-section" class="mt-6 bg-gray-50 p-3 rounded-md border border-gray-200">
        <h3 class="text-lg font-medium mb-3">Match Vendor</h3>
        
        <div class="mb-3">
          <p class="text-gray-700 mb-2">
            <span class="font-medium">Xero Vendor:</span> {currentBill.contact?.name || 'Unknown'}
          </p>
          
          <!-- Auto-matching status messages -->
          <div class="mb-2">
            {#if isAutoMatchingVendor}
              <div class="flex items-center">
                <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                <span class="text-sm text-blue-700">Searching Jobtread for Vendor</span>
              </div>
            {/if}
            
            {#if autoMatchedVendor}
              <div class="flex items-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
                <span class="text-sm text-green-700">Automatically found the Vendor in Jobtread!</span>
              </div>
              
              <!-- Auto-creating spinner -->
              <div class="flex items-center mt-2">
                <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                <span class="text-sm text-blue-700">Auto-creating vendor bill...</span>
              </div>
            {/if}
            
            {#if pendingVendorCandidates.length > 0}
              <div class="flex items-center mb-1">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span class="text-sm text-blue-700">We found close matches — please confirm</span>
              </div>
            {:else if autoMatchFailed}
              <div class="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span class="text-sm text-orange-700">We didn't find a matching Vendor choose one below</span>
              </div>
            {/if}
          </div>

          <!-- Candidate picker UI - shown when fuzzy matches found -->
          {#if pendingVendorCandidates.length > 0}
            <div class="mb-3 border border-blue-200 rounded-md bg-blue-50 p-3">
              <p class="text-sm text-gray-700 mb-2">
                Searching for: <span class="font-medium">"{pendingVendorSourceName}"</span>
              </p>
              <div class="space-y-2">
                {#each pendingVendorCandidates as candidate}
                  <div class="flex items-center justify-between bg-white rounded border border-gray-200 px-3 py-2">
                    <div>
                      <span class="text-sm font-medium text-gray-800">{candidate.name}</span>
                      <span class="text-xs text-gray-500 ml-2">Close match</span>
                    </div>
                    <button
                      class="px-3 py-1 text-xs font-medium bg-green-600 hover:bg-green-700 text-white rounded"
                      on:click={() => confirmVendorCandidate(candidate, pendingVendorSourceName)}
                    >
                      Confirm
                    </button>
                  </div>
                {/each}
              </div>
              <button
                class="mt-2 px-3 py-1 text-xs font-medium bg-gray-200 hover:bg-gray-300 text-gray-700 rounded"
                on:click={rejectVendorCandidates}
              >
                None of these
              </button>
            </div>
          {/if}
          
          <!-- ONLY show manual UI when auto-match failed -->
          {#if !autoMatchedVendor && !isAutoMatchingVendor && pendingVendorCandidates.length === 0}
            <!-- Add name to search button - shows after auto-matching completes -->
            {#if autoMatchFailed}
              <div class="mb-2">
                <button 
                  class="px-2 py-0.5 text-xs bg-gray-200 hover:bg-gray-300 text-gray-700 rounded inline-flex items-center"
                  on:click={() => {
                    const searchPrefix = (currentBill.contact?.name || '').substring(0, 5);
                    vendorSearchQuery = searchPrefix;
                    autoMatchedVendor = false;
                    autoMatchFailed = false;
                    filterVendors();
                  }}
                >
                  Add name to search
                </button>
              </div>
            {/if}
            
            <div class="relative">
              <input
                type="text"
                placeholder="Search for a vendor..."
                class="w-full px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 border border-gray-300"
                value={vendorSearchQuery}
                on:input={handleVendorSearch}
              />
              {#if isLoadingVendors}
                <div class="absolute right-3 top-2">
                  <div class="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                </div>
              {/if}
            </div>
          {/if}
        </div>
        
        <!-- ONLY show vendor list when auto-match failed -->
        {#if !autoMatchedVendor && !isAutoMatchingVendor && pendingVendorCandidates.length === 0}
          <div class="mb-3 max-h-60 overflow-y-auto border border-gray-200 rounded-md bg-white">
            {#if filteredVendors.length === 0}
              <p class="p-3 text-gray-500 text-center">No vendors found. Try a different search term or create a new vendor.</p>
            {:else}
              <div class="divide-y divide-[#edf0f7]">
                {#each filteredVendors.slice(0, 3) as vendor}
                  <div 
                    role="button"
                    tabindex="0"
                    class="p-3 hover:bg-gray-100 cursor-pointer flex items-center justify-between {selectedVendorId === vendor.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''}"
                    on:click={() => selectVendor(vendor.id)}
                    on:keydown={(e) => e.key === 'Enter' && selectVendor(vendor.id)}
                  >
                    <span>{vendor.name}</span>
                    {#if selectedVendorId === vendor.id}
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                      </svg>
                    {/if}
                  </div>
                {/each}
              </div>
            {/if}
          </div>
          
          <RequiredVendorCustomField
            fields={requiredVendorFields}
            bind:values={requiredVendorFieldValues}
            message={requiredVendorError}
          />

          <div class="flex justify-between">
            <button 
              on:click={cancelVendorMatching}
              class="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded text-sm"
            >
              Cancel
            </button>
            
            <div class="flex gap-3">
              <button 
                on:click={createNewVendor}
                disabled={isCreatingVendor || !requiredCustomFieldsFilled(requiredVendorFields, requiredVendorFieldValues)}
                class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded text-sm flex items-center"
              >
                {#if isCreatingVendor}
                  <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {/if}
                Create New Vendor from Xero Details
              </button>
              
              <button 
                on:click={createVendorBill}
                disabled={!selectedVendorId || isCreatingVendorBill}
                class="bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-2 px-4 rounded text-sm flex items-center {!selectedVendorId ? 'opacity-50 cursor-not-allowed' : ''}"
              >
                {#if isCreatingVendorBill}
                  <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {/if}
                Create Vendor Bill
              </button>
            </div>
          </div>
        {/if}
      </div>
    {/if}
  </div>
</Layout>

<!-- Error Modal -->
{#if showErrorModal && creationError}
  <div class="fixed z-50 inset-0 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <!-- Background overlay -->
      <div class="fixed inset-0 transition-opacity" aria-hidden="true">
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>
      
      <!-- Modal panel -->
      <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
        <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          <div class="sm:flex sm:items-start">
            <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
              <!-- Exclamation icon -->
              <svg class="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <h3 class="text-lg leading-6 font-medium text-gray-900">
                Error Creating Vendor Bill
              </h3>
              <div class="mt-2">
                <p class="text-sm text-gray-500">
                  {creationError}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
          <button 
            type="button" 
            on:click={closeErrorModal}
            class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
{/if}

<style>
  .process-selected-invoices {
    padding: 16px;
    height: calc(100vh - 80px);
  }
  .error {
    color: red;
    font-weight: bold;
  }
  .sticky-footer {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 10;
  }
  
  @media (min-width: 768px) {
    .sticky-footer {
      left: 16rem;
    }
  }

  /* Wide screens: bill card absolute-positioned on left, matching section centered */
  @media (min-width: 1670px) {
    .bill-layout {
      position: relative;
    }
    .bill-layout .bill-details {
      position: absolute;
      left: 0;
      top: 0;
      width: 300px;
    }
    .bill-layout .matched-vendor-bills {
      max-width: 48rem;
      margin-left: auto;
      margin-right: auto;
    }
    .bill-details-horizontal {
      display: none;
    }
    .bill-details-stacked {
      display: block;
    }
  }

  /* Narrow screens: stacked layout with horizontal bill card */
  @media (max-width: 1669px) {
    .bill-layout {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }
    .bill-layout .bill-details {
      width: 75%;
      min-width: 420px;
      max-width: 800px;
    }
    .bill-layout .matched-vendor-bills {
      width: 100%;
    }
    .bill-details-stacked {
      display: none;
    }
    .bill-details-horizontal {
      display: block;
    }
  }
</style>

